import React from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CHARACTERS, CharacterType, getRpsMultiplier } from '../data/characters';

interface GameState {
  player1Score: number;
  player2Score: number;
  gameStatus: 'playing' | 'ended';
  winner: 'red' | 'blue' | null;
}

export type PortalPongConfigPreset = 'light' | 'normal' | 'chaos';
export type WizardColorKey = 'teal' | 'cyan' | 'lavender' | 'darkPurple' | 'red' | 'blue' | 'yellow' | 'orange';

const WIZARD_COLORS: Record<WizardColorKey, { hex: number; label: string; hudClass: string }> = {
  teal: { hex: 0x14b8a6, label: 'Teal', hudClass: 'text-teal-300' },
  cyan: { hex: 0x22d3ee, label: 'Cyan', hudClass: 'text-cyan-300' },
  lavender: { hex: 0xc4b5fd, label: 'Lavender', hudClass: 'text-violet-200' },
  darkPurple: { hex: 0x6d28d9, label: 'Dark Purple', hudClass: 'text-violet-400' },
  red: { hex: 0xef4444, label: 'Red', hudClass: 'text-rose-300' },
  blue: { hex: 0x3b82f6, label: 'Blue', hudClass: 'text-blue-300' },
  yellow: { hex: 0xfacc15, label: 'Yellow', hudClass: 'text-yellow-300' },
  orange: { hex: 0xf97316, label: 'Orange', hudClass: 'text-orange-300' }
};

export interface PortalPongConfig {
  background: 'random' | 'bg1' | 'bg2' | 'bg3' | 'bg4' | 'bg5' | 'bg6' | 'bg7';
  preset: PortalPongConfigPreset;
  parallax: boolean;
  seed: number;
  player1Color?: WizardColorKey;
  player2Color?: WizardColorKey;
  player1Character?: CharacterType;
  player2Character?: CharacterType;
  aiDifficulty?: number;
  localPlayer?: 'player1' | 'player2';
  mode?: 'ai' | 'matchmaking';
  matchmakingRoom?: string;
  player1Id?: string;
  player2Id?: string;
}

export interface MatchResult {
  winner: 1 | 2 | null;
  p1Score: number;
  p2Score: number;
  isVsAi: boolean;
  player1Id: string;
  player2Id: string;
}

interface PortalPongGameProps {
  config?: PortalPongConfig;
  onExit?: () => void;
  onMatchEnd?: (result: MatchResult) => void;
}

interface Bounds {
  width: number;
  height: number;
}

interface ParallaxLayers {
  far: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  mid?: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  near?: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  edgeFog?: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
}

interface PortalVisual {
  group: THREE.Group;
  ring: THREE.Mesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  rim: THREE.Mesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  inner: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  glow: THREE.Sprite;
  fog: THREE.Sprite;
  pulseOffset: number;
}

// Sent by player1 to synchronize authoritative game state with player2
interface AuthorityState {
  ball: { x: number; y: number; vx: number; vy: number };
  score: { p1: number; p2: number };
  celebrating: boolean;
  matchEnded: boolean;
  winner: 1 | 2 | null;
  boxes: Array<{ id: number; x: number; y: number }>;
  pickups: Array<{ id: number; x: number; y: number; type: string }>;
}

interface ControllerFrame {
  left: boolean;
  right: boolean;
  down: boolean;
  jumpQueued: boolean;
  jumpHeld: boolean;
  shieldHeld: boolean;
  castQueued: boolean;
  aimX: number;
  aimY: number;
}

interface MobileStickState {
  active: boolean;
  x: number;
  y: number;
  jumpLatch?: boolean;
  fireQueued?: boolean;
}

const BG_IDS: Array<Exclude<PortalPongConfig['background'], 'random'>> = [
  'bg1',
  'bg2',
  'bg3',
  'bg4',
  'bg5',
  'bg6',
  'bg7'
];

// Side-platform pairs per preset: light=1 pair, normal=2, chaos=3
const PRESET_TO_PAIR_LIMIT: Record<PortalPongConfigPreset, number> = {
  light: 1,
  normal: 2,
  chaos: 3
};

const WIN_SCORE = 7;
const REMOTE_STALE_MS = 1600;
const POWERUP_DURATION = 60 * 60; // 60 seconds at 60fps

type PowerupType = 'tripleJump' | 'permaFlight' | 'tripleBlast' | 'reflector' | 'teleport' | 'doubleSize' | 'quarterSize' | 'homingShots' | 'telekinesis';
const ALL_POWERUPS: PowerupType[] = ['tripleJump', 'permaFlight', 'tripleBlast', 'reflector', 'teleport', 'doubleSize', 'quarterSize', 'homingShots', 'telekinesis'];
const POWERUP_COLORS: Record<PowerupType, number> = {
  tripleJump: 0x22c55e,
  permaFlight: 0x06b6d4,
  tripleBlast: 0xf97316,
  reflector: 0x3b82f6,
  teleport: 0xa855f7,
  doubleSize: 0xef4444,
  quarterSize: 0xfbbf24,
  homingShots: 0xec4899,
  telekinesis: 0x14b8a6
};
const POWERUP_LABELS: Record<PowerupType, string> = {
  tripleJump: 'Triple Jump',
  permaFlight: 'Perma Flight',
  tripleBlast: 'Triple Blast',
  reflector: 'Reflector',
  teleport: 'Teleport',
  doubleSize: 'Double Size',
  quarterSize: 'Mini Mode',
  homingShots: 'Homing',
  telekinesis: 'Telekinesis'
};

const buildRandom = (seed: number) => {
  let t = seed >>> 0;
  return () => {
    t += 0x6D2B79F5;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const randomBetween = (random: () => number, min: number, max: number) => min + random() * (max - min);
const chooseOne = <T,>(random: () => number, values: readonly T[]): T => values[Math.floor(random() * values.length)];
const jitterColor = (baseHex: number, random: () => number, spread = 0.12) => {
  const color = new THREE.Color(baseHex);
  color.offsetHSL(
    randomBetween(random, -0.02, 0.02),
    randomBetween(random, -spread * 0.25, spread * 0.35),
    randomBetween(random, -spread, spread)
  );
  return color;
};
const hexToCss = (hex: number) => `#${hex.toString(16).padStart(6, '0')}`;
const shortestWrappedDelta = (current: number, previous: number, span: number) => {
  let delta = current - previous;
  const halfSpan = span / 2;
  if (delta > halfSpan) {
    delta -= span;
  } else if (delta < -halfSpan) {
    delta += span;
  }
  return delta;
};

const resolveBackground = (background: PortalPongConfig['background'], random: () => number) => {
  if (background !== 'random') {
    return background;
  }
  return BG_IDS[Math.floor(random() * BG_IDS.length)];
};

class Platform {
  mesh: THREE.Mesh;
  top: number;
  bottom: number;
  left: number;
  right: number;
  tiltAngle: number;
  tiltNormal: THREE.Vector3;

  constructor(scene: THREE.Scene, x: number, y: number, width = 2, tiltAngle = 0) {
    this.tiltAngle = tiltAngle;
    // Surface normal of the top face after tilt (rotated from world-up by angle around Z)
    this.tiltNormal = new THREE.Vector3(-Math.sin(tiltAngle), Math.cos(tiltAngle), 0).normalize();

    const geometry = new THREE.BoxGeometry(width, 0.16, 0.78);
    const material = new THREE.MeshPhongMaterial({ color: 0x95a5a6 });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.set(x, y, 0);
    this.mesh.rotation.z = tiltAngle;
    scene.add(this.mesh);

    // Compute AABB of the rotated box so collision still works
    const hw = width / 2;
    const hh = 0.08;
    const ca = Math.abs(Math.cos(tiltAngle));
    const sa = Math.abs(Math.sin(tiltAngle));
    const aabbHalfW = hw * ca + hh * sa;
    const aabbHalfH = hw * sa + hh * ca;

    this.top    = y + aabbHalfH;
    this.bottom = y - aabbHalfH;
    this.left   = x - aabbHalfW;
    this.right  = x + aabbHalfW;
  }
}

type ProjectileStyle = 'magic' | 'axe' | 'slash' | 'disk' | 'arrow';

class Spell {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  lifetime: number;
  age: number;
  exploded: boolean;
  explosionRadius: number;
  explosionMesh?: THREE.Mesh;
  explosionMaterial?: THREE.ShaderMaterial;
  explosionFramesLeft: number;
  explosionTotalFrames: number;
  owner: 'player1' | 'player2';
  ballImpulseApplied: boolean;
  nudgedPlayers: Set<'player1' | 'player2'>;
  target: THREE.Vector3;
  tailMeshes: THREE.Mesh[];
  tailPoints: THREE.Vector3[];
  fizzleParticles: Array<{ mesh: THREE.Mesh; velocity: THREE.Vector3; life: number }>;
  trailParticles: Array<{ mesh: THREE.Mesh; velocity: THREE.Vector3; life: number; maxLife: number }>;
  scene: THREE.Scene;
  explosionScale: number;
  homingTargets: Array<{ position: THREE.Vector3 }> | null;
  spellColor: number;
  chainCount: number;
  pendingChains: Array<{ delay: number; offset: THREE.Vector3 }>;
  projectileStyle: ProjectileStyle;
  spinAxis: THREE.Vector3;

  constructor(
    scene: THREE.Scene,
    position: THREE.Vector3,
    direction: THREE.Vector3,
    target: THREE.Vector3,
    owner: 'player1' | 'player2',
    explosionScale = 1,
    spellColor = 0xb388ff,
    projectileStyle: ProjectileStyle = 'magic'
  ) {
    this.spellColor = spellColor;
    this.projectileStyle = projectileStyle;
    const headCol = new THREE.Color(spellColor);

    let geometry: THREE.BufferGeometry;
    let material: THREE.Material;

    switch (projectileStyle) {
      case 'axe': {
        const shape = new THREE.Shape();
        shape.moveTo(0, 0.18);
        shape.lineTo(-0.14, -0.09);
        shape.lineTo(0.14, -0.09);
        shape.closePath();
        geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.05, bevelEnabled: false });
        geometry.center();
        material = new THREE.MeshBasicMaterial({ color: headCol, transparent: true, opacity: 1.0 });
        break;
      }
      case 'slash': {
        const slashShape = new THREE.Shape();
        slashShape.moveTo(-0.22, 0);
        slashShape.quadraticCurveTo(0, 0.08, 0.22, 0);
        slashShape.quadraticCurveTo(0, -0.03, -0.22, 0);
        geometry = new THREE.ExtrudeGeometry(slashShape, { depth: 0.015, bevelEnabled: false });
        geometry.center();
        material = new THREE.MeshBasicMaterial({
          color: headCol.clone().lerp(new THREE.Color(0xffffff), 0.5),
          transparent: true, opacity: 0.9,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
        break;
      }
      case 'disk': {
        geometry = new THREE.TorusGeometry(0.1, 0.03, 8, 16);
        material = new THREE.MeshBasicMaterial({ color: headCol, transparent: true, opacity: 1.0 });
        break;
      }
      case 'arrow': {
        const arrowShape = new THREE.Shape();
        arrowShape.moveTo(0.22, 0);
        arrowShape.lineTo(-0.12, 0.025);
        arrowShape.lineTo(-0.18, 0.015);
        arrowShape.lineTo(-0.18, -0.015);
        arrowShape.lineTo(-0.12, -0.025);
        arrowShape.closePath();
        geometry = new THREE.ExtrudeGeometry(arrowShape, { depth: 0.02, bevelEnabled: false });
        geometry.center();
        const arrowCol = headCol.clone().lerp(new THREE.Color(0xffffff), 0.2);
        material = new THREE.MeshBasicMaterial({ color: arrowCol, transparent: true, opacity: 1.0 });
        break;
      }
      default: {
        geometry = new THREE.SphereGeometry(0.12, 12, 12);
        material = new THREE.MeshBasicMaterial({ color: headCol, transparent: true, opacity: 1.0 });
        break;
      }
    }

    this.mesh = new THREE.Mesh(geometry, material);
    
    const normalizedDirection = direction.clone().normalize();
    const spawnOffset = normalizedDirection.clone().multiplyScalar(0.7);
    spawnOffset.y += 0.35;
    this.mesh.position.copy(position).add(spawnOffset);

    this.target = target.clone();
    const toTarget = this.target.clone().sub(this.mesh.position);
    const targetDirection = toTarget.lengthSq() > 0.0001 ? toTarget.normalize() : normalizedDirection;
    const speed = projectileStyle === 'arrow' ? 0.52 : 0.36;
    this.velocity = targetDirection.multiplyScalar(speed);

    // Orient non-magic projectiles toward travel direction
    if (projectileStyle !== 'magic') {
      const angle = Math.atan2(this.velocity.y, this.velocity.x);
      this.mesh.rotation.z = angle;
    }

    // Spin axis: velocity direction for axe/disk, perpendicular for slash
    this.spinAxis = this.velocity.clone().normalize();
    
    this.lifetime = 70;
    this.age = 0;
    this.exploded = false;
    this.explosionScale = explosionScale;
    this.explosionRadius = 1.05 * explosionScale;
    this.explosionTotalFrames = 52;
    this.explosionFramesLeft = this.explosionTotalFrames;
    this.homingTargets = null;
    this.chainCount = 0;
    this.pendingChains = [];
    this.owner = owner;
    this.ballImpulseApplied = false;
    this.nudgedPlayers = new Set();
    this.tailMeshes = [];
    this.tailPoints = [];
    this.fizzleParticles = [];
    this.trailParticles = [];
    this.scene = scene;
    scene.add(this.mesh);

    if (projectileStyle === 'magic') {
      const tailCol = headCol.clone().lerp(new THREE.Color(0xffffff), 0.25);
      for (let i = 0; i < 12; i += 1) {
        const r = 0.09 - i * 0.006;
        const tail = new THREE.Mesh(
          new THREE.SphereGeometry(Math.max(0.015, r), 8, 8),
          new THREE.MeshBasicMaterial({
            color: tailCol.clone().lerp(new THREE.Color(0x000000), i * 0.04),
            transparent: true,
            opacity: 0.72 - i * 0.052,
            blending: THREE.AdditiveBlending,
            depthWrite: false
          })
        );
        this.tailMeshes.push(tail);
        this.tailPoints.push(this.mesh.position.clone());
        scene.add(tail);
      }
    } else if (projectileStyle === 'slash') {
      // Slash gets fading afterimage copies
      const afterCol = headCol.clone().lerp(new THREE.Color(0xffffff), 0.6);
      for (let i = 0; i < 5; i += 1) {
        const afterGeo = new THREE.PlaneGeometry(0.3 - i * 0.03, 0.06 - i * 0.008);
        const tail = new THREE.Mesh(afterGeo, new THREE.MeshBasicMaterial({
          color: afterCol,
          transparent: true,
          opacity: 0.5 - i * 0.09,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          side: THREE.DoubleSide,
        }));
        this.tailMeshes.push(tail);
        this.tailPoints.push(this.mesh.position.clone());
        scene.add(tail);
      }
    }
  }

  update(platforms: Platform[], ball: Ball, worldHalfWidth: number) {
    if (this.exploded) {
      this.explosionFramesLeft -= 1;
      if (this.explosionMesh && this.explosionMaterial) {
        this.explosionMesh.scale.addScalar(0.015);
        this.explosionMaterial.uniforms.uTime.value += 0.045;
        const life = Math.max(0, this.explosionFramesLeft / this.explosionTotalFrames);
        const progress = Math.pow(1 - life, 0.72);
        this.explosionMaterial.uniforms.uProgress.value = THREE.MathUtils.clamp(
          progress,
          0,
          1
        );
        this.explosionMaterial.uniforms.uOpacity.value = Math.pow(life, 0.58);
      }

      let allFizzled = true;
      this.fizzleParticles.forEach((particle) => {
        if (particle.life <= 0) {
          particle.mesh.visible = false;
          return;
        }
        allFizzled = false;
        particle.life -= 1;
        particle.velocity.y -= 0.00035;
        particle.mesh.position.add(particle.velocity);
        particle.mesh.scale.multiplyScalar(0.99);
        const material = particle.mesh.material as THREE.MeshBasicMaterial;
        material.opacity = Math.max(0, particle.life / 38);
      });

      if (this.explosionFramesLeft <= 0 && allFizzled) {
        this.cleanup();
        return true;
      }
      return false;
    }
    
    this.lifetime--;
    this.age += 1;
    if (this.lifetime <= 0) {
      this.explode();
      return false;
    }

    // Homing: steer toward nearest target
    if (this.homingTargets && this.homingTargets.length > 0) {
      let closest: THREE.Vector3 | null = null;
      let closestDist = Infinity;
      for (const t of this.homingTargets) {
        const d = this.mesh.position.distanceTo(t.position);
        if (d < closestDist) { closestDist = d; closest = t.position; }
      }
      if (closest) {
        const toTarget = closest.clone().sub(this.mesh.position).normalize();
        this.velocity.lerp(toTarget.multiplyScalar(0.36), 0.07);
      }
    }
    
    this.velocity.y -= this.projectileStyle === 'arrow' ? 0.0008 : 0.002;
    this.mesh.position.add(this.velocity);
    this.tailPoints.unshift(this.mesh.position.clone());
    if (this.tailPoints.length > this.tailMeshes.length) {
      this.tailPoints.length = this.tailMeshes.length;
    }

    // ── Projectile-specific spinning & trail ──────────────────────────────────
    const style = this.projectileStyle;

    if (style === 'axe') {
      // Spinning axe: tumble around the velocity axis
      const travelAngle = Math.atan2(this.velocity.y, this.velocity.x);
      this.mesh.rotation.z = travelAngle + this.age * 0.45;
    } else if (style === 'disk') {
      // Spinning disk: flat spin + wobble
      const travelAngle = Math.atan2(this.velocity.y, this.velocity.x);
      this.mesh.rotation.z = travelAngle;
      this.mesh.rotation.x = this.age * 0.5;
      this.mesh.rotation.y = this.age * 0.35;
    } else if (style === 'slash') {
      // Wind slash: slight oscillation, faces travel direction
      const travelAngle = Math.atan2(this.velocity.y, this.velocity.x);
      this.mesh.rotation.z = travelAngle + Math.sin(this.age * 0.3) * 0.15;
      this.mesh.scale.setScalar(1.0 + Math.sin(this.age * 0.4) * 0.1);
    } else if (style === 'arrow') {
      // Arrow: always points in flight direction, no spin
      const travelAngle = Math.atan2(this.velocity.y, this.velocity.x);
      this.mesh.rotation.z = travelAngle;
    }

    // Magic style: snake-wiggle tail
    if (style === 'magic') {
      const velocity2d = new THREE.Vector3(-this.velocity.y, this.velocity.x, 0);
      if (velocity2d.lengthSq() > 0.0001) velocity2d.normalize();

      this.tailMeshes.forEach((tail, idx) => {
        const point = this.tailPoints[Math.min(idx, this.tailPoints.length - 1)];
        const wiggleAmp = Math.max(0.005, 0.28 - idx * 0.022);
        const wigglePhase = this.age * 0.65 - idx * 0.55;
        const wiggle = Math.sin(wigglePhase) * wiggleAmp;
        tail.position.copy(point).addScaledVector(velocity2d, wiggle);
        const mat = tail.material as THREE.MeshBasicMaterial;
        mat.opacity = Math.max(0.03, 0.65 - idx * 0.052);
      });
    } else if (style === 'slash') {
      // Afterimage trail for slash
      this.tailMeshes.forEach((tail, idx) => {
        const point = this.tailPoints[Math.min(idx, this.tailPoints.length - 1)];
        tail.position.copy(point);
        tail.rotation.z = this.mesh.rotation.z;
        const mat = tail.material as THREE.MeshBasicMaterial;
        mat.opacity = Math.max(0.02, (0.4 - idx * 0.08) * (0.6 + Math.sin(this.age * 0.5) * 0.4));
      });
    }

    // Trail particles for all styles (different shapes per style)
    const trailCount = style === 'magic' ? 2 : 1;
    for (let p = 0; p < trailCount; p++) {
      const origin = style === 'magic'
        ? (this.tailPoints[Math.min(this.tailPoints.length - 1, 5 + p * 2)] ?? this.mesh.position)
        : this.mesh.position;
      let pGeo: THREE.BufferGeometry;
      if (style === 'axe') {
        pGeo = new THREE.TetrahedronGeometry(0.025 + Math.random() * 0.02, 0);
      } else if (style === 'slash') {
        pGeo = new THREE.PlaneGeometry(0.04 + Math.random() * 0.03, 0.01);
      } else if (style === 'disk') {
        pGeo = new THREE.RingGeometry(0.01, 0.025 + Math.random() * 0.015, 6);
      } else if (style === 'arrow') {
        pGeo = new THREE.ConeGeometry(0.012, 0.04 + Math.random() * 0.02, 4);
      } else {
        pGeo = new THREE.SphereGeometry(0.025 + Math.random() * 0.02, 5, 5);
      }
      const brightCol = new THREE.Color(this.spellColor).lerp(new THREE.Color(0xffffff), 0.3 + Math.random() * 0.4);
      const pMat = new THREE.MeshBasicMaterial({
        color: brightCol,
        transparent: true,
        opacity: 0.7 + Math.random() * 0.3,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const pMesh = new THREE.Mesh(pGeo, pMat);
      pMesh.position.copy(origin).add(
        new THREE.Vector3((Math.random() - 0.5) * 0.12, (Math.random() - 0.5) * 0.12, (Math.random() - 0.5) * 0.08)
      );
      if (style !== 'magic') pMesh.rotation.z = Math.random() * Math.PI * 2;
      const maxLife = 10 + Math.floor(Math.random() * 8);
      this.trailParticles.push({
        mesh: pMesh,
        velocity: new THREE.Vector3((Math.random() - 0.5) * 0.018, (Math.random() - 0.5) * 0.018 + 0.006, 0),
        life: maxLife,
        maxLife
      });
      this.scene.add(pMesh);
    }

    this.trailParticles = this.trailParticles.filter(tp => {
      tp.life--;
      if (tp.life <= 0) {
        this.scene.remove(tp.mesh);
        (tp.mesh.material as THREE.Material).dispose();
        tp.mesh.geometry.dispose();
        return false;
      }
      tp.mesh.position.add(tp.velocity);
      tp.velocity.y -= 0.0005;
      if (style === 'axe' || style === 'disk') tp.mesh.rotation.z += 0.15;
      const mat = tp.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = (tp.life / tp.maxLife) * 0.75;
      return true;
    });

    const toTarget = this.target.clone().sub(this.mesh.position);
    if (toTarget.length() <= Math.max(0.18, this.velocity.length() * 1.2)) {
      this.explode();
      return false;
    }

    if (this.mesh.position.y <= 0) {
      this.explode();
      return false;
    }

    const touchingPlatform = platforms.some((platform) => (
      this.mesh.position.x >= platform.left &&
      this.mesh.position.x <= platform.right &&
      this.mesh.position.y >= platform.bottom - 0.05 &&
      this.mesh.position.y <= platform.top + 0.05
    ));

    if (touchingPlatform || this.mesh.position.x > worldHalfWidth + 0.8 || this.mesh.position.x < -worldHalfWidth - 0.8) {
      this.explode();
      return false;
    }

    if (this.mesh.position.distanceTo(ball.mesh.position) <= 0.5) {
      this.explode();
    }

    return false;
  }

  explode() {
    if (this.exploded) {
      return;
    }
    this.exploded = true;
    const explosionGeo = new THREE.SphereGeometry(this.explosionRadius);
    const explosionMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uOpacity: { value: 0.75 },
        uProgress: { value: 0.04 },
        uSeed: { value: this.age * 0.137 + (this.owner === 'player1' ? 1.13 : 2.31) },
        uColorCore: { value: new THREE.Color(this.spellColor).lerp(new THREE.Color(0xffffff), 0.45) },
        uColorArc: { value: new THREE.Color(this.spellColor).multiplyScalar(0.9) },
        uColorHot: { value: new THREE.Color(this.spellColor).lerp(new THREE.Color(0xffffff), 0.75) }
      },
      vertexShader: `
        varying vec3 vLocalPos;
        void main() {
          vLocalPos = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uOpacity;
        uniform float uProgress;
        uniform float uSeed;
        uniform vec3 uColorCore;
        uniform vec3 uColorArc;
        uniform vec3 uColorHot;
        varying vec3 vLocalPos;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        void main() {
          vec3 p = normalize(vLocalPos);
          float radius = length(vLocalPos);
          float theta = atan(p.y, p.x);
          float phi = atan(p.z, length(p.xy));

          float electricField = noise(vec2(theta * 6.2 + uSeed + uTime * 2.6, phi * 9.0 - uTime * 1.7));
          electricField += noise(vec2(theta * 12.3 - uTime * 3.5, phi * 16.0 + uSeed));
          electricField *= 0.5;

          float front = smoothstep(0.0, 0.18, uProgress);
          float core = smoothstep(0.48, 0.0, radius) * (1.0 - uProgress * 0.8);
          float shock = exp(-abs(radius - uProgress) * 12.0) * front;
          float arc1 = pow(max(0.0, sin(theta * 13.0 + electricField * 4.5 + uTime * 8.8)), 12.0);
          float arc2 = pow(max(0.0, sin(phi * 18.0 - electricField * 5.7 - uTime * 10.7)), 10.0);
          float arcs = (arc1 + arc2) * shock;

          float energy = core * 0.55 + shock * (0.75 + electricField * 0.5) + arcs * 1.35;
          vec3 color = mix(uColorCore, uColorArc, clamp(shock + arcs * 0.35, 0.0, 1.0));
          color = mix(color, uColorHot, clamp(arcs + core * 0.7, 0.0, 1.0));

          float alpha = clamp(energy * uOpacity, 0.0, 1.0);
          gl_FragColor = vec4(color * (0.9 + shock * 0.5), alpha);
        }
      `
    });
    this.explosionMesh = new THREE.Mesh(explosionGeo, explosionMat);
    this.explosionMaterial = explosionMat;
    this.explosionMesh.position.copy(this.mesh.position);
    this.scene.add(this.explosionMesh);
    this.fizzleParticles = [];
    for (let i = 0; i < 11; i += 1) {
      const fCol = new THREE.Color(this.spellColor).lerp(new THREE.Color(0xffffff), i % 3 === 0 ? 0.7 : 0.3);
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(randomBetween(Math.random, 0.03, 0.055), 8, 8),
        new THREE.MeshBasicMaterial({
          color: fCol,
          transparent: true,
          opacity: 0.85,
          blending: THREE.AdditiveBlending,
          depthWrite: false
        })
      );
      particle.position.copy(this.mesh.position);
      const drift = new THREE.Vector3(
        randomBetween(Math.random, -0.03, 0.03),
        randomBetween(Math.random, 0.01, 0.055),
        0
      );
      this.fizzleParticles.push({
        mesh: particle,
        velocity: drift,
        life: Math.floor(randomBetween(Math.random, 18, 38))
      });
      this.scene.add(particle);
    }
    this.tailMeshes.forEach((tail) => {
      const mat = tail.material as THREE.MeshBasicMaterial;
      mat.opacity = 0;
      tail.visible = false;
    });
    // Kill trail particles on explode
    this.trailParticles.forEach(tp => {
      this.scene.remove(tp.mesh);
      (tp.mesh.material as THREE.MeshBasicMaterial).dispose();
      tp.mesh.geometry.dispose();
    });
    this.trailParticles = [];

    if (this.chainCount > 0) {
      for (let i = 0; i < this.chainCount; i++) {
        this.pendingChains.push({
          delay: 8 + i * 10 + Math.floor(Math.random() * 6),
          offset: new THREE.Vector3(
            (Math.random() - 0.5) * 3.2,
            (Math.random() - 0.3) * 2.0,
            0
          ),
        });
      }
      this.chainCount = 0;
    }
  }

  tickChains(): Spell[] {
    const spawned: Spell[] = [];
    this.pendingChains = this.pendingChains.filter(chain => {
      chain.delay--;
      if (chain.delay > 0) return true;
      const pos = (this.explosionMesh ?? this.mesh).position.clone().add(chain.offset);
      pos.y = Math.max(pos.y, 0.3);
      const child = new Spell(
        this.scene, pos,
        new THREE.Vector3(0, 1, 0), pos,
        this.owner, this.explosionScale, this.spellColor
      );
      child.lifetime = 1;
      child.age = 0;
      if (this.homingTargets) child.homingTargets = this.homingTargets;
      spawned.push(child);
      return false;
    });
    return spawned;
  }

  cleanup() {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    const projectileMaterial = this.mesh.material;
    if (Array.isArray(projectileMaterial)) {
      projectileMaterial.forEach((material) => material.dispose());
    } else {
      projectileMaterial.dispose();
    }
    this.tailMeshes.forEach((tail) => {
      this.scene.remove(tail);
      tail.geometry.dispose();
      const tailMaterial = tail.material;
      if (Array.isArray(tailMaterial)) {
        tailMaterial.forEach((material) => material.dispose());
      } else {
        tailMaterial.dispose();
      }
    });
    this.fizzleParticles.forEach((particle) => {
      this.scene.remove(particle.mesh);
      particle.mesh.geometry.dispose();
      const particleMaterial = particle.mesh.material;
      if (Array.isArray(particleMaterial)) {
        particleMaterial.forEach((material) => material.dispose());
      } else {
        particleMaterial.dispose();
      }
    });
    this.fizzleParticles = [];
    this.trailParticles.forEach(tp => {
      this.scene.remove(tp.mesh);
      (tp.mesh.material as THREE.MeshBasicMaterial).dispose();
      tp.mesh.geometry.dispose();
    });
    this.trailParticles = [];
    if (this.explosionMesh) {
      this.scene.remove(this.explosionMesh);
      this.explosionMesh.geometry.dispose();
      const explosionMaterial = this.explosionMesh.material;
      if (Array.isArray(explosionMaterial)) {
        explosionMaterial.forEach((material) => material.dispose());
      } else {
        explosionMaterial.dispose();
      }
      this.explosionMaterial = undefined;
    }
  }
}

class Ball {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  radius: number;

  constructor(scene: THREE.Scene) {
    this.radius = 0.3;
    const geometry = new THREE.SphereGeometry(this.radius);
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.mesh = new THREE.Mesh(geometry, material);
    this.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 0.12,
      (Math.random() - 0.5) * 0.12,
      0
    );
    this.mesh.position.set(0, 4.2, 0);
    scene.add(this.mesh);
  }

  update(gameHeight: number, worldHalfWidth: number, platforms: Platform[]) {
    const nextPosition = this.mesh.position.clone().add(this.velocity);

    // Check platform collisions
    platforms.forEach(platform => {
      // Ball's current bounds
      const ballBottom = this.mesh.position.y - 0.3;
      const ballTop = this.mesh.position.y + 0.3;
      const ballLeft = this.mesh.position.x - 0.3;
      const ballRight = this.mesh.position.x + 0.3;

      // Ball's next bounds
      const nextBallBottom = nextPosition.y - 0.3;
      const nextBallTop = nextPosition.y + 0.3;
      const nextBallLeft = nextPosition.x - 0.3;
      const nextBallRight = nextPosition.x + 0.3;

      // Tilt normal (default straight-up for walls)
      const platNormal: THREE.Vector3 = (platform as Platform).tiltNormal ?? new THREE.Vector3(0, 1, 0);

      // Collision checks
      if (nextBallRight >= platform.left && nextBallLeft <= platform.right) {
        // Vertical collision
        if (nextBallBottom <= platform.top && ballBottom > platform.top) {
          // Hitting from above — reflect off tilted surface normal
          nextPosition.y = platform.top + 0.3;
          const dot = this.velocity.dot(platNormal);
          this.velocity.addScaledVector(platNormal, -2 * dot * 0.72);
          // Clamp to avoid absurd speeds
          if (this.velocity.length() > 0.38) this.velocity.setLength(0.38);
        } else if (nextBallTop >= platform.bottom && ballTop < platform.bottom) {
          // Hitting platform from below
          nextPosition.y = platform.bottom - 0.3;
          this.velocity.y = Math.abs(this.velocity.y) * 0.65;
        }
      }

      if (nextBallTop >= platform.bottom && nextBallBottom <= platform.top) {
        // Horizontal collision
        if (nextBallRight >= platform.left && ballRight < platform.left) {
          nextPosition.x = platform.left - 0.3;
          this.velocity.x *= -0.72;
        } else if (nextBallLeft <= platform.right && ballLeft > platform.right) {
          nextPosition.x = platform.right + 0.3;
          this.velocity.x *= -0.72;
        }
      }
    });

    // Update position after collision checks
    this.mesh.position.copy(nextPosition);
    
    // Existing wrapping and bounds checks
    if (this.mesh.position.x > worldHalfWidth) this.mesh.position.x = -worldHalfWidth;
    if (this.mesh.position.x < -worldHalfWidth) this.mesh.position.x = worldHalfWidth;

    if (this.mesh.position.y > gameHeight - this.radius) {
      this.mesh.position.y = gameHeight - this.radius;
      this.velocity.y = -Math.abs(this.velocity.y);
    }

    // Hard clamp + bounce so the ball cannot sink and jitter in the floor.
    if (this.mesh.position.y < this.radius) {
      this.mesh.position.y = this.radius;
      this.velocity.y = Math.abs(this.velocity.y);
      if (this.velocity.y < 0.09) {
        this.velocity.y = 0.09;
      }
    }

    const speed = this.velocity.length();
    if (speed > 0.3) {
      this.velocity.multiplyScalar(0.3 / speed);
    }
  }

  bounce(normal: THREE.Vector3, speed = 1) {
    const dot = this.velocity.dot(normal);
    this.velocity.sub(normal.multiplyScalar(2 * dot));
    this.velocity.multiplyScalar(speed);
  }

  applyImpulse(impulse: THREE.Vector3) {
    this.velocity.add(impulse);
    const speed = this.velocity.length();
    if (speed < 0.12) {
      this.velocity.normalize().multiplyScalar(0.12);
    }
  }

  reset() {
    this.mesh.position.set(0, 4.2, 0);
    this.velocity.set(
      (Math.random() - 0.5) * 0.12,
      (Math.random() - 0.5) * 0.12,
      0
    );
  }
}

interface WizardRig {
  root: THREE.Group;
  hat: THREE.Mesh;
  wand: THREE.Mesh;
  glow: THREE.Mesh;
  baseWandRotationZ: number;
}

const createWizardAvatar = (color: number, isLeftSide: boolean): WizardRig => {
  const group = new THREE.Group();
  const robeColor = new THREE.Color(color).multiplyScalar(0.5);

  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.38, 18, 18),
    new THREE.MeshPhongMaterial({ color, shininess: 70 })
  );
  body.position.y = 0.37;
  group.add(body);

  const robe = new THREE.Mesh(
    new THREE.ConeGeometry(0.34, 0.56, 18),
    new THREE.MeshPhongMaterial({ color: robeColor, shininess: 35 })
  );
  robe.position.y = 0.05;
  group.add(robe);

  const hat = new THREE.Mesh(
    new THREE.ConeGeometry(0.28, 0.6, 18),
    new THREE.MeshPhongMaterial({ color: 0x222831, shininess: 10 })
  );
  hat.position.y = 0.95;
  group.add(hat);

  const wand = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.55, 10),
    new THREE.MeshPhongMaterial({ color: 0x4a2c17 })
  );
  const baseWandRotationZ = isLeftSide ? -0.5 : 0.5;
  wand.rotation.z = baseWandRotationZ;
  wand.position.set(isLeftSide ? -0.34 : 0.34, 0.32, 0.08);
  group.add(wand);

  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 12, 12),
    new THREE.MeshBasicMaterial({ color: 0xfff4a6, transparent: true, opacity: 0.85 })
  );
  glow.position.copy(wand.position).add(new THREE.Vector3(isLeftSide ? -0.03 : 0.03, 0.3, 0));
  group.add(glow);

  return {
    root: group,
    hat,
    wand,
    glow,
    baseWandRotationZ
  };
};

class Player {
  mesh: THREE.Group;
  rig: WizardRig;
  velocity: THREE.Vector3;
  onGround: boolean;
  direction: THREE.Vector3;
  pressingDown: boolean;
  spellCooldown: number;
  shieldActive: boolean;
  shieldMesh: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;
  shieldPulseTime: number;
  id: 'player1' | 'player2';
  jumpBufferFrames: number;
  coyoteFrames: number;
  jumpsUsed: number;
  jumpHeld: boolean;
  holdJumpFrames: number;
  flyActive: boolean;
  flyFuelFrames: number;
  flySputterPhase: number;
  facing: 1 | -1;
  wobbleTime: number;
  powerups: Partial<Record<PowerupType, number>>;
  baseColor: number;
  activePowerupLabel: string;
  /** The loaded GLB model group (null = still using procedural wizard avatar) */
  glbModel: THREE.Group | null;
  /** Running time for idle bob animation */
  bobTime: number;
  /** Squash scale: 1 normally, <1 on land, recovers over frames */
  squashY: number;
  /** Was on ground last frame (for landing detection) */
  wasOnGround: boolean;
  /** Animation mixer for rigged characters */
  mixer: THREE.AnimationMixer | null;
  /** Named animation actions keyed by state name */
  animActions: Record<string, THREE.AnimationAction>;
  /** Currently playing animation state name */
  currentAnimState: string;
  /** Multipliers from chosen character (default 1.0 for wizard) */
  statSpeed: number;
  statJump: number;
  statPower: number;
  statDefense: number;
  statCooldown: number;
  /** RPS damage multiplier vs the opponent's character */
  rpsMultiplier: number;
  characterType: CharacterType;
  /** Telekinesis grab: frames remaining in current grab (-1 = not grabbing) */
  tkGrabFrames: number;
  /** Telekinesis cooldown frames before next grab */
  tkCooldown: number;
  /** Telekinesis visual beam line */
  tkBeam: THREE.Line | null;

  constructor(
    scene: THREE.Scene,
    color: number,
    startX: number,
    id: 'player1' | 'player2',
    characterType: CharacterType = 'wizard',
    opponentCharacter: CharacterType = 'wizard'
  ) {
    this.characterType = characterType;
    const charDef = CHARACTERS[characterType];
    this.statSpeed    = charDef.stats.speed;
    this.statJump     = charDef.stats.jump;
    this.statPower    = charDef.stats.power;
    this.statDefense  = charDef.stats.defense;
    this.statCooldown = charDef.stats.cooldown;
    this.rpsMultiplier = getRpsMultiplier(characterType, opponentCharacter);

    this.rig = createWizardAvatar(color, startX < 0);
    this.mesh = this.rig.root;
    this.mesh.scale.setScalar(0.84 * charDef.stats.size);
    this.mesh.position.set(startX, 0.44, 0);
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.onGround = true;
    this.direction = new THREE.Vector3(1, 0, 0);
    this.pressingDown = false;
    this.spellCooldown = 0;
    this.shieldActive = false;
    this.shieldPulseTime = Math.random() * Math.PI * 2;
    this.id = id;
    this.jumpBufferFrames = 0;
    this.coyoteFrames = 0;
    this.jumpsUsed = 0;
    this.jumpHeld = false;
    this.holdJumpFrames = 0;
    this.flyActive = false;
    this.flyFuelFrames = 180; // ~3 seconds at 60fps
    this.flySputterPhase = Math.random() * Math.PI * 2;
    this.facing = startX < 0 ? 1 : -1;
    this.wobbleTime = Math.random() * Math.PI * 2;
    this.powerups = {};
    this.baseColor = color;
    this.activePowerupLabel = '';
    this.glbModel = null;
    this.bobTime = Math.random() * Math.PI * 2;
    this.squashY = 1;
    this.wasOnGround = true;
    this.mixer = null;
    this.animActions = {};
    this.currentAnimState = 'idle';
    this.tkGrabFrames = -1;
    this.tkCooldown = 0;
    this.tkBeam = null;
    this.mesh.rotation.y = this.facing === 1 ? 0 : Math.PI;

    this.shieldMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.64, 20, 20),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
    );
    this.shieldMesh.visible = false;
    this.shieldMesh.position.set(0, 0.45, 0);
    this.mesh.add(this.shieldMesh);
    scene.add(this.mesh);
  }

  hasPowerup(type: PowerupType): boolean {
    return (this.powerups[type] ?? 0) > 0;
  }

  grantPowerup(type: PowerupType) {
    this.powerups[type] = POWERUP_DURATION;
    this.activePowerupLabel = POWERUP_LABELS[type];
  }

  update(platforms: Platform[], worldHalfWidth: number) {
    // Tick powerup timers
    let anyActive = false;
    let lastLabel = '';
    for (const key of ALL_POWERUPS) {
      if ((this.powerups[key] ?? 0) > 0) {
        this.powerups[key] = (this.powerups[key] as number) - 1;
        anyActive = true;
        lastLabel = POWERUP_LABELS[key];
      }
    }
    this.activePowerupLabel = anyActive ? lastLabel : '';

    // Double size: scale 2x, speed 0.5x; quarter size: scale 0.5x
    const charSizeBase = 0.84 * CHARACTERS[this.characterType].stats.size;
    const sizeMultiplier = this.hasPowerup('doubleSize') ? 2 : this.hasPowerup('quarterSize') ? 0.5 : 1;
    const targetScale = charSizeBase * sizeMultiplier;
    this.mesh.scale.setScalar(targetScale);

    // Reflector: auto shield while active
    if (this.hasPowerup('reflector')) {
      this.shieldActive = true;
    }

    const playerRadius = this.hasPowerup('doubleSize') ? 0.88 : 0.44;
    const prevPosition = this.mesh.position.clone();

    if (this.spellCooldown > 0) {
      this.spellCooldown -= 1;
    }
    if (this.tkCooldown > 0) {
      this.tkCooldown -= 1;
    }
    this.shieldPulseTime += 0.11;
    if (this.jumpBufferFrames > 0) {
      this.jumpBufferFrames -= 1;
    }
    if (this.onGround) {
      this.coyoteFrames = 7;
      this.jumpsUsed = 0;
      this.flyActive = false;
      this.flyFuelFrames = 180;
    } else if (this.coyoteFrames > 0) {
      this.coyoteFrames -= 1;
    }

    if (!this.onGround || this.velocity.y > 0) {
      const canFlyNow = !this.onGround && this.jumpsUsed >= 2 && this.jumpHeld && this.flyFuelFrames > 0;
      if (canFlyNow) {
        this.flyActive = true;
      } else if (!this.jumpHeld || this.flyFuelFrames <= 0) {
        this.flyActive = false;
      }

      if (this.flyActive) {
        // Sputtery flight: intermittent lift bursts rather than smooth hover.
        this.flySputterPhase += 0.43;
        const sputter = Math.sin(this.flySputterPhase) * 0.55 + (Math.random() - 0.5) * 0.45;
        const lift = 0.006 + Math.max(0, sputter) * 0.012;
        this.velocity.y += lift;
        this.velocity.y -= 0.0055;
        this.velocity.y = THREE.MathUtils.clamp(this.velocity.y, -0.04, 0.135);
        this.velocity.x *= 0.985;
        // Perma flight never depletes fuel
        if (!this.hasPowerup('permaFlight')) {
          this.flyFuelFrames = Math.max(0, this.flyFuelFrames - 1);
        }
      } else {
        // Classic platformer jump shaping: hold to stretch arc, tap for short hop.
        const canApplyHold = this.jumpHeld && this.holdJumpFrames > 0 && this.velocity.y > 0;
        if (canApplyHold) {
          // Hold adds roughly one-third extra jump height per jump.
          this.velocity.y += 0.0052;
          this.holdJumpFrames -= 1;
        }
        this.velocity.y -= 0.014;
      }
    }

    const maxAirJumps = this.hasPowerup('tripleJump') ? 2 : 1;
    const canUseGroundJump = this.onGround || this.coyoteFrames > 0;
    const canUseAirJump = this.jumpsUsed < maxAirJumps + 1 && !canUseGroundJump;
    if (this.jumpBufferFrames > 0 && (canUseGroundJump || canUseAirJump)) {
      const quickJumpVelocity = 0.315 * this.statJump;
      this.velocity.y = quickJumpVelocity;
      this.jumpBufferFrames = 0;
      this.coyoteFrames = 0;
      this.onGround = false;
      this.jumpsUsed = canUseGroundJump ? 1 : this.jumpsUsed + 1;
      this.holdJumpFrames = 9;
    }

    const nextPosition = this.mesh.position.clone().add(this.velocity);

    this.onGround = false;
    platforms.forEach(platform => {
      const landingLeniency = 0.14;
      const sideLeniency = 0.22;
      const playerLeft = nextPosition.x - playerRadius + sideLeniency;
      const playerRight = nextPosition.x + playerRadius - sideLeniency;
      const playerTop = nextPosition.y + playerRadius;
      const playerBottom = nextPosition.y - playerRadius;
      const wasFeetAbove = prevPosition.y - playerRadius >= platform.top - landingLeniency;
      const isFalling = this.velocity.y <= 0;
      const overlapsX = playerRight >= platform.left && playerLeft <= platform.right;

      if (overlapsX) {
        if (
          !this.pressingDown &&
          isFalling &&
          wasFeetAbove &&
          playerBottom <= platform.top + landingLeniency
        ) {
          nextPosition.y = platform.top + playerRadius;
          this.velocity.y = 0;
          this.onGround = true;
          // Gentle slope slide: push player downhill proportional to tilt
          const tilt = (platform as Platform).tiltAngle ?? 0;
          if (Math.abs(tilt) > 0.02) {
            this.velocity.x += Math.sin(tilt) * 0.004;
          }
        } else if (
          playerTop >= platform.bottom &&
          prevPosition.y + playerRadius <= platform.bottom + 0.02 &&
          this.velocity.y > 0
        ) {
          nextPosition.y = platform.bottom - playerRadius;
          this.velocity.y = 0;
        }
      }

      if (playerTop >= platform.bottom && playerBottom <= platform.top) {
        if (playerRight >= platform.left && 
            prevPosition.x + playerRadius <= platform.left) {
          nextPosition.x = platform.left - playerRadius;
          this.velocity.x = 0;
        } else if (playerLeft <= platform.right && 
                   prevPosition.x - playerRadius >= platform.right) {
          nextPosition.x = platform.right + playerRadius;
          this.velocity.x = 0;
        }
      }
    });

    this.mesh.position.copy(nextPosition);

    if (this.mesh.position.y < 0) {
      this.mesh.position.y = 0;
      this.velocity.y = 0;
      this.onGround = true;
      this.flyActive = false;
      this.flyFuelFrames = this.hasPowerup('permaFlight') ? 180 : 180;
    }

    if (this.mesh.position.x > worldHalfWidth) this.mesh.position.x = -worldHalfWidth;
    if (this.mesh.position.x < -worldHalfWidth) this.mesh.position.x = worldHalfWidth;

    this.velocity.x *= 0.78;

    const horizontalSpeed = Math.abs(this.velocity.x);

    // ── Landing squash/stretch ────────────────────────────────────────────────
    const justLanded = this.onGround && !this.wasOnGround;
    if (justLanded) {
      // Impact squash — heavier the fall, stronger the squash
      const fallSpeed = Math.abs(prevPosition.y - this.mesh.position.y);
      this.squashY = Math.max(0.55, 1 - fallSpeed * 8);
    }
    this.wasOnGround = this.onGround;
    // Recover squash toward 1
    if (this.squashY < 1) this.squashY = Math.min(1, this.squashY + 0.08);
    // Stretch upward during fast upward movement
    const airStretch = !this.onGround && this.velocity.y > 0.1 ? 1 + this.velocity.y * 1.8 : 1;
    const finalScaleY = this.squashY * airStretch;

    if (this.glbModel) {
      // ── GLB model procedural animation ─────────────────────────────────────
      this.bobTime += 0.045 + horizontalSpeed * 0.3;

      // Lean in direction of movement
      const lean = THREE.MathUtils.clamp(-this.velocity.x * 5, -0.35, 0.35);
      this.glbModel.rotation.z = THREE.MathUtils.lerp(this.glbModel.rotation.z, lean, 0.18);

      // Squash/stretch on the model group itself
      this.glbModel.scale.y = THREE.MathUtils.lerp(this.glbModel.scale.y, finalScaleY, 0.25);
      this.glbModel.scale.x = THREE.MathUtils.lerp(this.glbModel.scale.x, 1 / finalScaleY, 0.25);

      // Facing direction
      const targetFacingY = this.facing === 1 ? 0 : Math.PI;
      this.glbModel.rotation.y = THREE.MathUtils.lerp(this.glbModel.rotation.y, targetFacingY, 0.25);

      // ── Animation state machine ────────────────────────────────────────────
      if (this.mixer) {
        // Determine desired animation state
        let desiredState = 'idle';
        if (!this.onGround && this.velocity.y > 0.05) {
          desiredState = 'jump';
        } else if (this.spellCooldown > Math.round(16 * this.statCooldown) - 4) {
          desiredState = 'cast';
        } else if (horizontalSpeed > 0.015) {
          desiredState = horizontalSpeed > 0.045 ? 'run' : 'walk';
        }

        if (desiredState !== this.currentAnimState && this.animActions[desiredState]) {
          const fadeTime = desiredState === 'jump' || desiredState === 'cast' ? 0.1 : 0.2;
          const prev = this.animActions[this.currentAnimState];
          const next = this.animActions[desiredState];
          if (prev) prev.fadeOut(fadeTime);
          next.reset().fadeIn(fadeTime).play();
          this.currentAnimState = desiredState;
        }

        this.mixer.update(1 / 60);
      } else {
        // No mixer (rig failed) — keep procedural bob
        const bob = Math.sin(this.bobTime) * 0.04;
        const targetBobY = this.onGround ? bob : 0;
        this.glbModel.position.y = THREE.MathUtils.lerp(this.glbModel.position.y, targetBobY, 0.2);
      }
    } else {
      // ── Procedural wizard avatar animation ─────────────────────────────────
      this.wobbleTime += 0.08 + horizontalSpeed * 0.6;
      const wobble = Math.sin(this.wobbleTime) * Math.min(0.12, horizontalSpeed * 0.5 + 0.03);
      this.rig.hat.rotation.z = wobble;
      this.rig.hat.rotation.x = Math.cos(this.wobbleTime * 1.4) * 0.04;
      this.rig.wand.rotation.z = this.rig.baseWandRotationZ + wobble * 1.8;
      this.rig.wand.rotation.x = Math.sin(this.wobbleTime * 1.9) * 0.1;
      this.rig.glow.position.y = 0.62 + Math.abs(wobble) * 0.2;

      // Multiply the uniform scale by squash/stretch factors
      this.mesh.scale.y *= finalScaleY;
      this.mesh.scale.x *= (1 / finalScaleY);
    }

    if (this.shieldActive) {
      this.shieldMesh.visible = true;
      this.shieldMesh.material.opacity = 0.2 + Math.sin(this.shieldPulseTime) * 0.05;
      this.shieldMesh.scale.setScalar(1 + Math.sin(this.shieldPulseTime * 1.7) * 0.02);
    } else {
      this.shieldMesh.material.opacity = Math.max(0, this.shieldMesh.material.opacity - 0.06);
      if (this.shieldMesh.material.opacity <= 0.01) {
        this.shieldMesh.visible = false;
      }
    }
  }

  queueJump() {
    this.jumpBufferFrames = 8;
  }

  moveLeft() {
    const sizeSp = this.hasPowerup('doubleSize') ? 0.5 : 1;
    const sp = sizeSp * this.statSpeed;
    this.velocity.x = Math.max(this.velocity.x - 0.018 * sp, -0.085 * sp);
    this.direction.x = -1;
    this.facing = -1;
    this.mesh.rotation.y = Math.PI;
  }

  moveRight() {
    const sizeSp = this.hasPowerup('doubleSize') ? 0.5 : 1;
    const sp = sizeSp * this.statSpeed;
    this.velocity.x = Math.min(this.velocity.x + 0.018 * sp, 0.085 * sp);
    this.direction.x = 1;
    this.facing = 1;
    this.mesh.rotation.y = 0;
  }

  setJumpHeld(isHeld: boolean) {
    this.jumpHeld = isHeld;
  }

  setShieldHeld(isHeld: boolean) {
    this.shieldActive = isHeld;
  }

  isFlying() {
    return this.flyActive;
  }

  applyKnockback(impulse: THREE.Vector3) {
    // Higher defense = less knockback received
    const reduced = impulse.clone().multiplyScalar(1 / this.statDefense);
    this.velocity.add(reduced);
    this.velocity.x = THREE.MathUtils.clamp(this.velocity.x, -0.19, 0.19);
    this.velocity.y = Math.min(this.velocity.y, 0.36);
  }

  setAimDirection(worldTarget: THREE.Vector3) {
    const toTarget = worldTarget.clone().sub(this.mesh.position);
    if (toTarget.lengthSq() < 0.01) {
      return;
    }
    toTarget.normalize();
    this.direction.copy(toTarget);
    if (Math.abs(this.direction.x) > 0.01) {
      this.direction.x = Math.sign(this.direction.x);
    }
    this.direction.y = THREE.MathUtils.clamp(this.direction.y, -0.4, 0.75);
  }

  get projectileStyle(): ProjectileStyle {
    switch (this.characterType) {
      case 'berserker': return 'axe';
      case 'knight':    return 'slash';
      case 'rogue':     return 'disk';
      case 'archer':    return 'arrow';
      default:          return 'magic';
    }
  }

  castSpell(scene: THREE.Scene, target: THREE.Vector3) {
    if (this.spellCooldown > 0) return null;
    this.spellCooldown = 16;
    return new Spell(scene, this.mesh.position, this.direction.clone(), target, this.id, 1, this.baseColor, this.projectileStyle);
  }

  castSpells(scene: THREE.Scene, target: THREE.Vector3, homingTargets?: Array<{ position: THREE.Vector3 }>): Spell[] {
    if (this.spellCooldown > 0) return [];
    this.spellCooldown = Math.round(16 * this.statCooldown);

    const explosionScale = (this.hasPowerup('doubleSize') ? 2 : 1) * this.statPower;
    const homing = this.hasPowerup('homingShots');
    const pStyle = this.projectileStyle;

    if (this.hasPowerup('tripleBlast')) {
      const s = new Spell(scene, this.mesh.position, this.direction.clone(), target, this.id, explosionScale, this.baseColor, pStyle);
      s.chainCount = 2;
      if (homing && homingTargets) s.homingTargets = homingTargets;
      return [s];
    }
    const s = new Spell(scene, this.mesh.position, this.direction.clone(), target, this.id, explosionScale, this.baseColor, pStyle);
    if (homing && homingTargets) s.homingTargets = homingTargets;
    return [s];
  }
}

// Add a new class for moving walls
class MovingWall implements Platform {
  mesh: THREE.Mesh;
  baseY: number;
  amplitude: number;
  frequency: number;
  top: number = 0;
  bottom: number = 0;
  left: number;
  right: number;
  tiltAngle = 0;
  tiltNormal = new THREE.Vector3(0, 1, 0);

  constructor(scene: THREE.Scene, x: number, baseY: number = 2) {
    const geometry = new THREE.BoxGeometry(0.4, 3.3, 0.8);
    const material = new THREE.MeshPhongMaterial({ color: 0x808080 });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.set(x, baseY, 0);
    scene.add(this.mesh);

    this.baseY = baseY;
    this.amplitude = 2;
    this.frequency = 0.5;

    // Set initial bounds
    this.left = x - 0.2;
    this.right = x + 0.2;
    
    // Initial position update
    this.updateBounds(0);
  }

  updateBounds(time: number) {
    const yOffset = Math.sin(time * this.frequency) * this.amplitude;
    this.mesh.position.y = this.baseY + yOffset;
    this.top = this.mesh.position.y + 1.65;
    this.bottom = this.mesh.position.y - 1.65;
  }
}

// ─── Mystery Box ─────────────────────────────────────────────────────────────

const MYSTERY_BOX_VERT = `
  varying vec2 vUv;
  varying vec3 vNormal;
  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const MYSTERY_BOX_FRAG = `
  uniform float uTime;
  varying vec2 vUv;
  varying vec3 vNormal;
  vec3 hsl2rgb(float h, float s, float l) {
    float c = (1.0 - abs(2.0*l - 1.0)) * s;
    float x = c * (1.0 - abs(mod(h*6.0, 2.0) - 1.0));
    float m = l - c*0.5;
    vec3 rgb;
    if      (h < 1.0/6.0) rgb = vec3(c,x,0.0);
    else if (h < 2.0/6.0) rgb = vec3(x,c,0.0);
    else if (h < 3.0/6.0) rgb = vec3(0.0,c,x);
    else if (h < 4.0/6.0) rgb = vec3(0.0,x,c);
    else if (h < 5.0/6.0) rgb = vec3(x,0.0,c);
    else                   rgb = vec3(c,0.0,x);
    return rgb + vec3(m);
  }
  float noise(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  void main() {
    vec2 uv = vUv;
    float t = uTime * 0.4;
    float blob1 = sin(uv.x * 3.5 + t * 1.3) * cos(uv.y * 2.8 - t * 0.9);
    float blob2 = sin(uv.y * 4.2 - t * 1.1 + 3.14) * cos(uv.x * 3.1 + t * 0.7);
    float blob3 = sin((uv.x + uv.y) * 3.0 + t * 0.6);
    float lava = (blob1 + blob2 + blob3) * 0.33;
    float h = fract(lava * 0.5 + t * 0.12);
    float s = 0.85;
    float l = 0.45 + lava * 0.12;
    vec3 col = hsl2rgb(h, s, l);
    float rim = 1.0 - max(dot(vNormal, vec3(0.0, 0.0, 1.0)), 0.0);
    col += rim * rim * 0.6;
    gl_FragColor = vec4(col, 0.93);
  }
`;

class MysteryBox {
  group: THREE.Group;
  material: THREE.ShaderMaterial;
  opened: boolean;
  openAge: number;
  scene: THREE.Scene;
  position: THREE.Vector3;
  id: number;

  constructor(scene: THREE.Scene, x: number, y: number, id = 0) {
    this.scene = scene;
    this.opened = false;
    this.openAge = 0;
    this.id = id;
    this.position = new THREE.Vector3(x, y, 0);

    this.material = new THREE.ShaderMaterial({
      vertexShader: MYSTERY_BOX_VERT,
      fragmentShader: MYSTERY_BOX_FRAG,
      uniforms: { uTime: { value: 0 } },
      transparent: true,
      side: THREE.DoubleSide
    });

    this.group = new THREE.Group();
    const cube = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.7), this.material);
    this.group.add(cube);

    // Question mark sprite
    const qCanvas = document.createElement('canvas');
    qCanvas.width = 64; qCanvas.height = 64;
    const ctx2d = qCanvas.getContext('2d')!;
    ctx2d.fillStyle = 'white';
    ctx2d.font = 'bold 48px Arial';
    ctx2d.textAlign = 'center';
    ctx2d.textBaseline = 'middle';
    ctx2d.fillText('?', 32, 34);
    const qTex = new THREE.CanvasTexture(qCanvas);
    const qSprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: qTex, transparent: true, opacity: 0.9, depthWrite: false })
    );
    qSprite.scale.setScalar(0.55);
    qSprite.position.z = 0.36;
    this.group.add(qSprite);

    this.group.position.copy(this.position);
    scene.add(this.group);
  }

  update(dt: number) {
    this.material.uniforms.uTime.value += dt;
    this.group.rotation.y += 0.018;
    this.group.rotation.x += 0.009;
    // Bob
    this.group.position.y = this.position.y + Math.sin(this.material.uniforms.uTime.value * 1.4) * 0.15;
  }

  open() {
    if (this.opened) return;
    this.opened = true;
    // Burst: spawn small colored cubes flying outward
    for (let i = 0; i < 12; i++) {
      const geo = new THREE.BoxGeometry(0.12, 0.12, 0.12);
      const mat = new THREE.MeshBasicMaterial({
        color: new THREE.Color().setHSL(Math.random(), 1, 0.55),
        transparent: true, opacity: 0.9
      });
      const frag = new THREE.Mesh(geo, mat);
      frag.position.copy(this.group.position);
      const angle = (i / 12) * Math.PI * 2;
      const spd = 0.12 + Math.random() * 0.1;
      const vel = new THREE.Vector3(Math.cos(angle) * spd, Math.sin(angle) * spd + 0.1, (Math.random() - 0.5) * 0.08);
      this.scene.add(frag);
      let life = 30;
      const tick = () => {
        if (life-- <= 0) { this.scene.remove(frag); geo.dispose(); mat.dispose(); return; }
        frag.position.add(vel);
        vel.y -= 0.006;
        (frag.material as THREE.MeshBasicMaterial).opacity = life / 30;
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    this.cleanup();
  }

  cleanup() {
    this.scene.remove(this.group);
    this.material.dispose();
  }
}

// ─── Powerup Pickup ───────────────────────────────────────────────────────────

class PowerupPickup {
  mesh: THREE.Mesh;
  glowMesh: THREE.Mesh;
  type: PowerupType;
  position: THREE.Vector3;
  collected: boolean;
  age: number;
  scene: THREE.Scene;
  labelSprite: THREE.Sprite;
  _modelGroup?: THREE.Group;
  id: number;

  constructor(scene: THREE.Scene, position: THREE.Vector3, type: PowerupType, id = 0) {
    this.scene = scene;
    this.type = type;
    this.position = position.clone();
    this.collected = false;
    this.age = 0;
    this.id = id;

    const color = POWERUP_COLORS[type];
    const geo = new THREE.OctahedronGeometry(0.28, 0);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.copy(this.position);
    scene.add(this.mesh);

    // Try loading the GLB icon — replaces octahedron if available
    const loader = new GLTFLoader();
    loader.load(
      `/models/powerup_${type}.glb`,
      (gltf) => {
        if (this.collected) return;
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        model.scale.setScalar(0.56 / maxDim);
        scene.remove(this.mesh);
        this._modelGroup = model;
        this._modelGroup.position.copy(this.position);
        scene.add(this._modelGroup);
      },
      undefined,
      () => { /* keep octahedron on error */ }
    );

    const glowGeo = new THREE.SphereGeometry(0.42, 16, 16);
    const glowMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.18,
      blending: THREE.AdditiveBlending, depthWrite: false
    });
    this.glowMesh = new THREE.Mesh(glowGeo, glowMat);
    this.glowMesh.position.copy(this.position);
    scene.add(this.glowMesh);

    // Label
    const lCanvas = document.createElement('canvas');
    lCanvas.width = 256; lCanvas.height = 48;
    const lCtx = lCanvas.getContext('2d')!;
    lCtx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    lCtx.font = 'bold 22px Arial';
    lCtx.textAlign = 'center';
    lCtx.textBaseline = 'middle';
    lCtx.fillText(POWERUP_LABELS[type], 128, 24);
    const lTex = new THREE.CanvasTexture(lCanvas);
    this.labelSprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: lTex, transparent: true, depthWrite: false })
    );
    this.labelSprite.scale.set(2.2, 0.42, 1);
    this.labelSprite.position.copy(this.position).add(new THREE.Vector3(0, 0.65, 0));
    scene.add(this.labelSprite);
  }

  update(dt: number) {
    this.age += dt;
    const bob = Math.sin(this.age * 2.2) * 0.12;
    const y = this.position.y + bob;
    if (this._modelGroup) {
      this._modelGroup.position.y = y;
      this._modelGroup.rotation.y += 0.04;
    } else {
      this.mesh.position.y = y;
      this.mesh.rotation.y += 0.04;
    }
    this.glowMesh.position.y = y;
    this.glowMesh.scale.setScalar(1 + Math.sin(this.age * 3.1) * 0.07);
    this.labelSprite.position.y = y + 0.65;
  }

  collect() {
    if (this.collected) return;
    this.collected = true;
    if (this._modelGroup) {
      this.scene.remove(this._modelGroup);
    } else {
      this.scene.remove(this.mesh);
    }
    this.scene.remove(this.glowMesh);
    this.scene.remove(this.labelSprite);
    (this.mesh.material as THREE.MeshBasicMaterial).dispose();
    (this.glowMesh.material as THREE.MeshBasicMaterial).dispose();
  }
}

// ─── Powerup icon characters ─────────────────────────────────────────────────
const POWERUP_ICON: Record<PowerupType, string> = {
  tripleJump:  '3↑',
  permaFlight: '∞',
  tripleBlast: '3✦',
  reflector:   '◈',
  teleport:    '⬡',
  doubleSize:  '2×',
  quarterSize: '¼',
  homingShots: '⊙',
  telekinesis: '🧠'
};

const PowerupBadge: React.FC<{ type: PowerupType; framesLeft: number }> = ({ type, framesLeft }) => {
  const colorHex = POWERUP_COLORS[type];
  const colorCss = `#${colorHex.toString(16).padStart(6, '0')}`;
  const r = 22;
  const pad = 5;
  const sz = (r + pad) * 2;
  const cx = r + pad;
  const cy = r + pad;
  const circ = 2 * Math.PI * r;
  const progress = Math.max(0, framesLeft / POWERUP_DURATION);
  const dash = progress * circ;
  const isLow = progress < 0.2;
  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: sz, height: sz, opacity: isLow ? undefined : 1 }}
    >
      {/* Pulsing low-time glow */}
      {isLow && (
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{ backgroundColor: `${colorCss}22`, animationDuration: '0.9s' }}
        />
      )}
      <svg width={sz} height={sz} style={{ position: 'absolute', inset: 0 }}>
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
        {/* Depleting ring */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={colorCss}
          strokeWidth="3.5"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ filter: `drop-shadow(0 0 5px ${colorCss})`, transition: 'stroke-dasharray 0.1s linear' }}
        />
      </svg>
      {/* Icon */}
      <div
        className="rounded-full flex items-center justify-center font-black select-none leading-none"
        style={{
          width: r * 2 - 4,
          height: r * 2 - 4,
          fontSize: '11px',
          color: colorCss,
          backgroundColor: `${colorCss}1a`,
          border: `1px solid ${colorCss}50`,
          backdropFilter: 'blur(6px)',
          textShadow: `0 0 8px ${colorCss}`,
          boxShadow: `inset 0 0 10px ${colorCss}30`,
        }}
      >
        {POWERUP_ICON[type]}
      </div>
      {/* Tooltip label on hover */}
      <div
        className="absolute -top-7 left-1/2 -translate-x-1/2 text-[9px] font-bold whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 px-1.5 py-0.5 rounded"
        style={{ color: colorCss, backgroundColor: 'rgba(0,0,0,0.7)' }}
      >
        {POWERUP_LABELS[type]}
      </div>
    </div>
  );
};

const PortalPongGame: React.FC<PortalPongGameProps> = ({ config, onExit, onMatchEnd }) => {
  const mountRef = React.useRef<HTMLDivElement>(null);
  const resumeFromPauseRef = React.useRef<() => void>(() => {});
  const exitMatchRef = React.useRef<() => void>(() => {});
  const leftJoystickRef = React.useRef<HTMLDivElement>(null);
  const rightJoystickRef = React.useRef<HTMLDivElement>(null);
  const moveStickRef = React.useRef<MobileStickState>({ active: false, x: 0, y: 0, jumpLatch: false });
  const aimStickRef = React.useRef<MobileStickState>({ active: false, x: 0, y: 0, fireQueued: false });
  const shieldTouchRef = React.useRef(false);
  const [matchSeedBump, setMatchSeedBump] = React.useState(0);
  const [chosenBackground, setChosenBackground] = React.useState<Exclude<PortalPongConfig['background'], 'random'> | null>(null);
  const [connectionStatus, setConnectionStatus] = React.useState('Offline AI');
  const [mobileControlsEnabled, setMobileControlsEnabled] = React.useState(false);
  const [moveStickUi, setMoveStickUi] = React.useState({ active: false, x: 0, y: 0 });
  const [aimStickUi, setAimStickUi] = React.useState({ active: false, x: 0, y: 0 });
  const [shieldButtonActive, setShieldButtonActive] = React.useState(false);
  const [pauseMenuOpen, setPauseMenuOpen] = React.useState(false);
  const [resumeCountdown, setResumeCountdown] = React.useState<number | null>(null);
  const [roundCountdownText, setRoundCountdownText] = React.useState<string | null>(null);
  const [goalCelebrationActive, setGoalCelebrationActive] = React.useState(false);
  const [localPowerups, setLocalPowerups] = React.useState<Partial<Record<PowerupType, number>>>({});
  const [gameState, setGameState] = React.useState<GameState>({
    player1Score: 0,
    player2Score: 0,
    gameStatus: 'playing',
    winner: null
  });

  const mergedConfig = React.useMemo<PortalPongConfig>(() => ({
    background: config?.background ?? 'random',
    preset: config?.preset ?? 'normal',
    parallax: config?.parallax ?? true,
    seed: (config?.seed ?? 42) + matchSeedBump,
    player1Color: config?.player1Color ?? 'cyan',
    player2Color: config?.player2Color ?? 'lavender',
    player1Character: config?.player1Character ?? 'wizard',
    player2Character: config?.player2Character ?? 'wizard',
    aiDifficulty: THREE.MathUtils.clamp(config?.aiDifficulty ?? 3, 1, 10),
    localPlayer: config?.localPlayer ?? 'player1',
    mode: config?.mode ?? 'ai',
    matchmakingRoom: config?.matchmakingRoom ?? '',
    player1Id: config?.player1Id ?? '',
    player2Id: config?.player2Id ?? '',
  }), [config, matchSeedBump]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    setMobileControlsEnabled(window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);
  }, []);

  const updateStickFromPointer = (
    e: React.PointerEvent<HTMLDivElement>,
    padRef: React.RefObject<HTMLDivElement>,
    stickRef: React.MutableRefObject<MobileStickState>,
    setUi: React.Dispatch<React.SetStateAction<{ active: boolean; x: number; y: number }>>
  ) => {
    const pad = padRef.current;
    if (!pad) {
      return;
    }
    const rect = pad.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    let dx = e.clientX - centerX;
    let dy = e.clientY - centerY;
    const maxRadius = Math.min(rect.width, rect.height) * 0.36;
    const magnitude = Math.hypot(dx, dy);
    if (magnitude > maxRadius) {
      const scale = maxRadius / magnitude;
      dx *= scale;
      dy *= scale;
    }
    const normalizedX = THREE.MathUtils.clamp(dx / maxRadius, -1, 1);
    const normalizedY = THREE.MathUtils.clamp(dy / maxRadius, -1, 1);
    stickRef.current.active = true;
    stickRef.current.x = normalizedX;
    stickRef.current.y = normalizedY;
    setUi({ active: true, x: dx, y: dy });
  };

  const resetStick = (
    stickRef: React.MutableRefObject<MobileStickState>,
    setUi: React.Dispatch<React.SetStateAction<{ active: boolean; x: number; y: number }>>,
    queueFire = false
  ) => {
    if (queueFire) {
      const magnitude = Math.hypot(stickRef.current.x, stickRef.current.y);
      if (magnitude > 0.35) {
        stickRef.current.fireQueued = true;
      }
    }
    stickRef.current.active = false;
    stickRef.current.x = 0;
    stickRef.current.y = 0;
    setUi({ active: false, x: 0, y: 0 });
  };

  React.useEffect(() => {
    if (!mountRef.current) return;

    const currentMount = mountRef.current;
    let frameHandle = 0;
    const random = buildRandom(mergedConfig.seed);
    const selectedBackground = resolveBackground(mergedConfig.background, random);
    setChosenBackground(selectedBackground);
    setGameState({
      player1Score: 0,
      player2Score: 0,
      gameStatus: 'playing',
      winner: null
    });
    setPauseMenuOpen(false);
    setResumeCountdown(null);
    setRoundCountdownText('3');
    setGoalCelebrationActive(false);
    
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.set(0, 5, 10);
    
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    currentMount.appendChild(renderer.domElement);

    const getViewBounds = () => {
      const vFOV = THREE.MathUtils.degToRad(camera.fov);
      const height = 2 * Math.tan(vFOV / 2) * Math.abs(camera.position.z);
      const width = height * camera.aspect;
      return { width, height };
    };

    const sizeRenderer = () => {
      const width = currentMount.clientWidth;
      const height = currentMount.clientHeight;
      camera.aspect = Math.max(width / height, 0.1);
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    sizeRenderer();

    const texture = new THREE.Texture();
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.repeat.set(0.56, 0.66);
    new THREE.ImageLoader().load(
      `/${selectedBackground}.png`,
      (image) => {
        texture.image = image;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
      },
      undefined,
      () => {}
    );

    const setupParallax = (bounds: Bounds): ParallaxLayers => {
      const far = new THREE.Mesh(
        new THREE.PlaneGeometry(bounds.width * 3.1, bounds.height * 2.9),
        new THREE.MeshBasicMaterial({ map: texture, opacity: 0.9, transparent: true })
      );
      far.position.set(0, 3.48, -10.1);
      scene.add(far);

      if (!mergedConfig.parallax) {
        return { far };
      }

      const midTexture = texture.clone();
      midTexture.wrapS = THREE.RepeatWrapping;
      midTexture.wrapT = THREE.ClampToEdgeWrapping;
      midTexture.repeat.set(0.66, 0.74);
      midTexture.colorSpace = THREE.SRGBColorSpace;
      const mid = new THREE.Mesh(
        new THREE.PlaneGeometry(bounds.width * 3.35, bounds.height * 3.0),
        new THREE.MeshBasicMaterial({
          map: midTexture,
          transparent: true,
          opacity: 0.36
        })
      );
      mid.position.set(0, 3.44, -9.5);
      scene.add(mid);

      const nearTexture = texture.clone();
      nearTexture.wrapS = THREE.RepeatWrapping;
      nearTexture.wrapT = THREE.ClampToEdgeWrapping;
      nearTexture.repeat.set(0.76, 0.84);
      nearTexture.colorSpace = THREE.SRGBColorSpace;
      const near = new THREE.Mesh(
        new THREE.PlaneGeometry(bounds.width * 3.6, bounds.height * 3.15),
        new THREE.MeshBasicMaterial({
          map: nearTexture,
          transparent: true,
          opacity: 0.24
        })
      );
      near.position.set(0, 3.4, -8.9);
      scene.add(near);

      const edgeFog = new THREE.Mesh(
        new THREE.PlaneGeometry(bounds.width * 3.0, bounds.height * 2.95),
        new THREE.ShaderMaterial({
          transparent: true,
          uniforms: {
            vignetteStrength: { value: 0.62 }
          },
          vertexShader: `
            varying vec2 vUv;
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            varying vec2 vUv;
            uniform float vignetteStrength;
            void main() {
              float horizontal = smoothstep(0.0, 0.18, vUv.x) * (1.0 - smoothstep(0.82, 1.0, vUv.x));
              float vertical = smoothstep(0.05, 0.32, vUv.y);
              float mask = horizontal * vertical;
              float alpha = (1.0 - mask) * vignetteStrength;
              gl_FragColor = vec4(vec3(0.0), alpha);
            }
          `
        })
      );
      edgeFog.position.set(0, 3.2, -5.2);
      scene.add(edgeFog);

      return { far, mid, near, edgeFog };
    };

    const viewBounds = getViewBounds();
    let gameHeight = viewBounds.height - 1;
    let ballSpawnY = THREE.MathUtils.clamp(gameHeight * 0.62, 3.4, 5.8);
    let worldHalfWidth = THREE.MathUtils.clamp(viewBounds.width * 0.5, 8.2, 13.5);
    let goalCenterX = worldHalfWidth - 1;
    let goalTriggerX = goalCenterX - 0.5;
    const parallaxLayers = setupParallax(viewBounds);

    const playerSpawnX = THREE.MathUtils.clamp(worldHalfWidth - 3.2, 3.8, 7.2);
    const player1Theme = WIZARD_COLORS[mergedConfig.player1Color ?? 'cyan'];
    const player2Theme = WIZARD_COLORS[mergedConfig.player2Color ?? 'lavender'];
    const p1Char = mergedConfig.player1Character ?? 'wizard';
    const p2Char = mergedConfig.player2Character ?? 'wizard';
    const player1 = new Player(scene, player1Theme.hex, -playerSpawnX, 'player1', p1Char, p2Char);
    const player2 = new Player(scene, player2Theme.hex, playerSpawnX, 'player2', p2Char, p1Char);

    // Async: swap wizard avatar for loaded GLB character model (non-blocking)
    /** Apply toon-shaded theme coloring to every mesh in a loaded model */
    const colorizeModel = (model: THREE.Object3D, charDef: typeof CHARACTERS['wizard'], baseColor: number) => {
      const themeColor  = new THREE.Color(charDef.themeColor);
      const accentColor = new THREE.Color(charDef.accentColor);
      let meshIdx = 0;
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          const col = meshIdx % 3 === 2 ? accentColor : themeColor;
          meshIdx++;
          const tinted = col.clone().lerp(new THREE.Color(baseColor), 0.45);
          mesh.material = new THREE.MeshToonMaterial({
            color: tinted,
            emissive: tinted.clone().multiplyScalar(0.12),
          });
          mesh.castShadow = false;
          mesh.receiveShadow = false;
        }
      });
    };

    /** Normalize a loaded model to fit a bounding box and sit at y=0 */
    const normalizeModel = (model: THREE.Object3D, height = 0.92) => {
      const box    = new THREE.Box3().setFromObject(model);
      const size   = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const s = height / maxDim;
      model.scale.setScalar(s);
      const box2 = new THREE.Box3().setFromObject(model);
      model.position.y -= box2.min.y;
    };

    /** Replace wizard avatar with the loaded model group */
    const swapAvatar = (player: Player, model: THREE.Group) => {
      const toRemove = player.mesh.children.filter(c => c !== player.shieldMesh);
      toRemove.forEach(c => player.mesh.remove(c));
      player.mesh.add(model);
      player.glbModel = model;
      player.rig.hat  = { rotation: { z: 0, x: 0 } } as unknown as THREE.Mesh;
      player.rig.wand = { rotation: { z: 0, x: 0 }, position: new THREE.Vector3() } as unknown as THREE.Mesh;
      player.rig.glow = { position: new THREE.Vector3() } as unknown as THREE.Mesh;
    };

    const loadCharacterModel = (player: Player, charType: CharacterType) => {
      const charDef = CHARACTERS[charType];
      const loader  = new GLTFLoader();
      const rigDir  = `/models/${charType}`;

      // Try loading the rigged+animated version first
      loader.load(
        `${rigDir}/rigged.glb`,
        (riggedGltf) => {
          const model = riggedGltf.scene;
          colorizeModel(model, charDef, player.baseColor);
          normalizeModel(model);
          swapAvatar(player, model);

          // Set up AnimationMixer on the rigged model
          player.mixer = new THREE.AnimationMixer(model);

          // Collect clips from the rigged GLB itself (may have none)
          const clips: Record<string, THREE.AnimationClip> = {};
          riggedGltf.animations.forEach(clip => { clips[clip.name] = clip; });

          // Load additional animation GLBs and register their clips
          const animFiles: Array<{ state: string; file: string }> = [
            { state: 'idle', file: `${rigDir}/idle.glb` },
            { state: 'walk', file: `${rigDir}/walk.glb` },
            { state: 'run',  file: `${rigDir}/run.glb` },
            { state: 'jump', file: `${rigDir}/jump.glb` },
            { state: 'cast', file: `${rigDir}/cast.glb` },
            { state: 'hit',  file: `${rigDir}/hit.glb` },
          ];

          let loaded = 0;
          const onAllLoaded = () => {
            // Register actions from collected clips
            for (const [state, clip] of Object.entries(clips)) {
              const action = player.mixer!.clipAction(clip);
              if (state === 'idle' || state === 'walk' || state === 'run') {
                action.loop = THREE.LoopRepeat;
              } else {
                action.loop = THREE.LoopOnce;
                action.clampWhenFinished = true;
              }
              player.animActions[state] = action;
            }

            // Start playing idle
            if (player.animActions['idle']) {
              player.animActions['idle'].play();
              player.currentAnimState = 'idle';
            }
          };

          animFiles.forEach(({ state, file }) => {
            loader.load(
              file,
              (animGltf) => {
                if (animGltf.animations.length > 0) {
                  clips[state] = animGltf.animations[0];
                }
                loaded++;
                if (loaded === animFiles.length) onAllLoaded();
              },
              undefined,
              () => {
                loaded++;
                if (loaded === animFiles.length) onAllLoaded();
              }
            );
          });
        },
        undefined,
        () => {
          // No rigged version — fall back to plain GLB (procedural animation)
          loader.load(
            charDef.modelPath,
            (gltf) => {
              const model = gltf.scene;
              colorizeModel(model, charDef, player.baseColor);
              normalizeModel(model);
              swapAvatar(player, model);
            },
            undefined,
            () => { /* keep wizard avatar */ }
          );
        }
      );
    };

    // Load character models for both players
    loadCharacterModel(player1, p1Char);
    loadCharacterModel(player2, p2Char);
    const localRole = mergedConfig.localPlayer === 'player2' ? 'player2' : 'player1';
    const activePlayer = localRole === 'player2' ? player2 : player1;
    const controlledByAi = localRole === 'player1' ? player2 : player1;
    const ball = new Ball(scene);
    let activeSpells: Spell[] = [];
    let activeMysteryBoxes: MysteryBox[] = [];
    let activePowerupPickups: PowerupPickup[] = [];
    let mysteryBoxNextSpawnAt = Date.now() + 15000; // first spawn 15s in
    let boxIdCounter = 0;
    let pickupIdCounter = 0;
    // Last authoritative ball state from player1, used for per-frame correction
    let lastAuthBall: { x: number; y: number; vx: number; vy: number } | null = null;
    let lastAuthBallAt = 0;
    let matchEnded = false;
    let pausedForMenu = false;
    let countdownEndAt = 0;
    let lastCountdownValue: number | null = null;
    let goalCelebrationEndAt = 0;
    let pendingRoundCountdownAfterGoal = false;
    let goalCelebrationUiShown = false;
    let roundCountdownEndAt = 0;
    let lastRoundCountdownText = '';
    let remoteLastSeenAt = 0;
    let lastConnectionLabel = '';
    let remoteJumpSeq = 0;
    let remoteCastSeq = 0;
    let aiCastCooldown = 0;
    let aiReactionFrames = 0;
    let networkSendBusy = false;
    let networkPollBusy = false;
    let lastNetworkSendAt = 0;
    let lastNetworkPollAt = 0;
    let player1ScoreLocal = 0;
    let player2ScoreLocal = 0;
    let horizontalWrapSpan = worldHalfWidth * 2;
    let parallaxFocusX = (player1.mesh.position.x + player2.mesh.position.x + ball.mesh.position.x * 2) / 4;
    let parallaxPhaseX = 0;
    let parallaxSmoothX = 0;
    let lastBallY = ball.mesh.position.y;
    let parallaxPhaseY = 0;
    let parallaxSmoothY = 0;

    const randTilt = (maxDeg: number) => randomBetween(random, -maxDeg, maxDeg) * (Math.PI / 180);

    // Any platform whose Y overlaps this window must stay clear of the portal mouth
    const PORTAL_Y_MIN = 1.0;
    const PORTAL_Y_MAX = 3.8;
    // Keep platform edges at least this far (world units) from the portal center
    const PORTAL_CLEAR_DIST = 2.4;

    const generatePlatforms = () => {
      const platforms: Platform[] = [];
      const pairCount = PRESET_TO_PAIR_LIMIT[mergedConfig.preset];
      const minY = 1.35;
      const maxY = Math.max(minY + 2.5, gameHeight - 0.7);
      // Enforce wide vertical breathing room between tiers
      const tierSpan = maxY - minY;
      const minTierGap = tierSpan / (pairCount + 1) * 1.1;

      // Single low center platform below portal zone
      platforms.push(new Platform(scene, 0, randomBetween(random, 2.2, 3.0), randomBetween(random, 1.8, 2.6), randTilt(8)));

      let lastY = minY;
      for (let i = 0; i < pairCount; i++) {
        const laneT = (i + 1) / (pairCount + 1);
        const maxWidth = THREE.MathUtils.clamp(worldHalfWidth * 0.26, 1.8, 3.0);
        const width = randomBetween(random, 1.5, maxWidth);
        const minX = 2.5;

        // Spread evenly with upward bias so top platforms actually reach the top
        const rawLaneY = minY + Math.pow(laneT, 0.8) * tierSpan;
        const clampedMin = Math.max(rawLaneY - 0.4, lastY + minTierGap);
        const clampedMax = Math.min(rawLaneY + 0.6, maxY);
        const y = THREE.MathUtils.clamp(
          randomBetween(random, clampedMin, Math.max(clampedMin + 0.05, clampedMax)),
          minY, maxY
        );
        lastY = y;

        const inPortalZone = y + 0.3 > PORTAL_Y_MIN && y - 0.3 < PORTAL_Y_MAX;
        const portalSafeEdge = inPortalZone ? goalCenterX - PORTAL_CLEAR_DIST : worldHalfWidth;
        const maxX = Math.max(minX + 0.25, Math.min(worldHalfWidth - width / 2 - 1.0, portalSafeEdge - width / 2));

        const x = randomBetween(random, minX, maxX);
        const tilt = randTilt(12);
        platforms.push(new Platform(scene, x, y, width, tilt));
        platforms.push(new Platform(scene, -x, y, width, -tilt));
      }

      // One optional high center platform for upper-area play
      if (random() > 0.35) {
        const y = randomBetween(random, maxY * 0.6, maxY);
        const width = randomBetween(random, 1.3, 2.0);
        platforms.push(new Platform(scene, randomBetween(random, -0.9, 0.9), y, width, randTilt(10)));
      }
      return platforms;
    };

    const platforms = generatePlatforms();

    const leftWall = new MovingWall(scene, -worldHalfWidth + 0.5);
    const rightWall = new MovingWall(scene, worldHalfWidth - 0.5);
    
    platforms.push(leftWall);
    platforms.push(rightWall);

    const portalFogTexture = (() => {
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const gradient = ctx.createRadialGradient(64, 64, 8, 64, 64, 64);
        gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
        gradient.addColorStop(0.35, 'rgba(255,255,255,0.5)');
        gradient.addColorStop(0.8, 'rgba(255,255,255,0.08)');
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 128, 128);
      }
      const textureFromCanvas = new THREE.CanvasTexture(canvas);
      textureFromCanvas.colorSpace = THREE.SRGBColorSpace;
      return textureFromCanvas;
    })();

    const createPortal = (x: number): PortalVisual => {
      const portalColor = x < 0 ? player1Theme.hex : player2Theme.hex;
      const group = new THREE.Group();
      group.position.set(x, 2.5, 0.05);
      group.rotation.y = x < 0 ? Math.PI / 2 : -Math.PI / 2;

      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.74, 0.1, 16, 48),
        new THREE.MeshBasicMaterial({
          color: portalColor,
          transparent: true,
          opacity: 0.7,
          blending: THREE.AdditiveBlending
        })
      );
      ring.scale.set(1, 1.2, 1);
      group.add(ring);

      const rim = new THREE.Mesh(
        new THREE.TorusGeometry(0.78, 0.04, 12, 48),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.45,
          blending: THREE.AdditiveBlending
        })
      );
      rim.scale.set(1, 1.2, 1);
      group.add(rim);

      const inner = new THREE.Mesh(
        new THREE.RingGeometry(0.35, 0.62, 64, 1),
        new THREE.MeshBasicMaterial({
          color: portalColor,
          transparent: true,
          opacity: 0.28,
          blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide
        })
      );
      inner.rotation.z = randomBetween(random, 0, Math.PI * 2);
      inner.scale.set(1, 1.18, 1);
      group.add(inner);

      const glow = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: portalFogTexture,
          color: portalColor,
          transparent: true,
          opacity: 0.35,
          blending: THREE.AdditiveBlending,
          depthWrite: false
        })
      );
      glow.scale.set(1.9, 2.2, 1);
      glow.position.set(0, 0, -0.03);
      group.add(glow);

      const fog = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: portalFogTexture,
          color: 0xffffff,
          transparent: true,
          opacity: 0.15,
          blending: THREE.AdditiveBlending,
          depthWrite: false
        })
      );
      fog.scale.set(2.7, 2.95, 1);
      fog.position.set(0, -0.12, -0.08);
      group.add(fog);

      scene.add(group);
      return {
        group,
        ring,
        rim,
        inner,
        glow,
        fog,
        pulseOffset: random() * Math.PI * 2
      };
    };

    const portals: PortalVisual[] = [createPortal(-goalCenterX), createPortal(goalCenterX)];

    const groundStyle = chooseOne(random, ['rock', 'grass', 'water'] as const);
    const styleSeed = randomBetween(random, -100, 100);
    const styleColors = (
      groundStyle === 'water'
        ? {
            c1: jitterColor(0x1f4a7a, random, 0.1),
            c2: jitterColor(0x3aa6d8, random, 0.12),
            c3: jitterColor(0x7ce5ff, random, 0.1)
          }
        : groundStyle === 'grass'
          ? {
              c1: jitterColor(0x2c6b2f, random, 0.12),
              c2: jitterColor(0x5ba846, random, 0.15),
              c3: jitterColor(0xa9d76c, random, 0.1)
            }
          : {
              c1: jitterColor(0x4e524d, random, 0.1),
              c2: jitterColor(0x777b76, random, 0.12),
              c3: jitterColor(0x9ea59b, random, 0.1)
            }
    );
    const floorUniforms = {
      uTime: { value: 0 },
      uStyle: { value: groundStyle === 'rock' ? 0 : groundStyle === 'grass' ? 1 : 2 },
      uSeed: { value: styleSeed },
      uBumpAmp: { value: groundStyle === 'water' ? 0.2 : 0.32 },
      uBumpFreq: { value: groundStyle === 'water' ? 4.8 : 6.8 },
      uColorA: { value: styleColors.c1 },
      uColorB: { value: styleColors.c2 },
      uColorC: { value: styleColors.c3 }
    };
    const floorMaterial = new THREE.ShaderMaterial({
      uniforms: floorUniforms,
      vertexShader: `
        uniform float uTime;
        uniform float uStyle;
        uniform float uSeed;
        uniform float uBumpAmp;
        uniform float uBumpFreq;
        varying vec2 vUv;
        varying float vHeight;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        void main() {
          vUv = uv;
          vec3 transformed = position;
          vec2 p = uv * uBumpFreq + vec2(uSeed * 0.1, uSeed * 0.07);
          float n1 = noise(p);
          float n2 = noise(p * 1.9 + 2.3);
          float n3 = noise(p * 3.4 - 4.1);
          float terrain = (n1 * 0.55 + n2 * 0.3 + n3 * 0.15) * 2.0 - 1.0;
          float ripple = sin((uv.x * 11.0 + uTime * 1.6) + uSeed) * 0.4
            + cos((uv.y * 13.0 - uTime * 1.9) - uSeed) * 0.3;
          float waterBlend = step(1.5, uStyle);
          float bump = mix(terrain, ripple, waterBlend) * uBumpAmp;
          transformed.z += bump;
          vHeight = bump;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uStyle;
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform vec3 uColorC;
        varying vec2 vUv;
        varying float vHeight;

        void main() {
          float bands = smoothstep(-0.24, 0.2, vHeight);
          vec3 base = mix(uColorA, uColorB, bands);
          float grain = fract(sin(dot(vUv * 127.0, vec2(12.9898, 78.233))) * 43758.5453);
          base = mix(base, uColorC, smoothstep(0.35, 0.9, grain));

          if (uStyle > 1.5) {
            float shimmer = 0.5 + 0.5 * sin((vUv.x * 30.0 + vUv.y * 18.0) - uTime * 3.2);
            base += vec3(0.05, 0.09, 0.12) * shimmer;
          } else if (uStyle > 0.5) {
            float moss = smoothstep(0.3, 0.8, fract(sin(dot(vUv * 83.0, vec2(5.19, 12.17))) * 9258.0));
            base = mix(base, uColorC, moss * 0.2);
          }

          gl_FragColor = vec4(base, 1.0);
        }
      `
    });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(26, 11, 200, 80), floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -1, 0);
    scene.add(floor);
    floor.scale.x = (worldHalfWidth / 10) * 1.35;
    floor.scale.y = 1.45;

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(0, 10, 0);
    scene.add(directionalLight);

    const applyResponsiveLayout = () => {
      const bounds = getViewBounds();
      gameHeight = bounds.height - 1;
      ballSpawnY = THREE.MathUtils.clamp(gameHeight * 0.62, 3.4, 5.8);
      worldHalfWidth = THREE.MathUtils.clamp(bounds.width * 0.5, 8.2, 13.5);
      goalCenterX = worldHalfWidth - 1;
      goalTriggerX = goalCenterX - 0.5;
      horizontalWrapSpan = worldHalfWidth * 2;

      floor.scale.x = (worldHalfWidth / 10) * 1.35;
      floor.scale.y = 1.45;

      leftWall.mesh.position.x = -worldHalfWidth + 0.5;
      leftWall.left = leftWall.mesh.position.x - 0.25;
      leftWall.right = leftWall.mesh.position.x + 0.25;
      rightWall.mesh.position.x = worldHalfWidth - 0.5;
      rightWall.left = rightWall.mesh.position.x - 0.25;
      rightWall.right = rightWall.mesh.position.x + 0.25;

      portals[0].group.position.x = -goalCenterX;
      portals[1].group.position.x = goalCenterX;
      player1.mesh.position.x = THREE.MathUtils.clamp(player1.mesh.position.x, -worldHalfWidth + 0.9, worldHalfWidth - 0.9);
      player2.mesh.position.x = THREE.MathUtils.clamp(player2.mesh.position.x, -worldHalfWidth + 0.9, worldHalfWidth - 0.9);
      ball.mesh.position.x = THREE.MathUtils.clamp(ball.mesh.position.x, -worldHalfWidth + ball.radius, worldHalfWidth - ball.radius);
    };
    applyResponsiveLayout();

    const localInput: ControllerFrame = {
      left: false,
      right: false,
      down: false,
      jumpQueued: false,
      jumpHeld: false,
      shieldHeld: false,
      castQueued: false,
      aimX: activePlayer.mesh.position.x + (localRole === 'player1' ? 2 : -2),
      aimY: activePlayer.mesh.position.y + 0.4
    };
    const remoteInput: ControllerFrame = {
      left: false,
      right: false,
      down: false,
      jumpQueued: false,
      jumpHeld: false,
      shieldHeld: false,
      castQueued: false,
      aimX: controlledByAi.mesh.position.x + (localRole === 'player1' ? -2 : 2),
      aimY: controlledByAi.mesh.position.y + 0.4
    };

    const clearLocalInput = () => {
      localInput.left = false;
      localInput.right = false;
      localInput.down = false;
      localInput.jumpHeld = false;
      localInput.shieldHeld = false;
      localInput.jumpQueued = false;
      localInput.castQueued = false;
      shieldTouchRef.current = false;
      setShieldButtonActive(false);
      moveStickRef.current.jumpLatch = false;
      moveStickRef.current.fireQueued = false;
      aimStickRef.current.fireQueued = false;
      resetStick(moveStickRef, setMoveStickUi);
      resetStick(aimStickRef, setAimStickUi);
    };

    const ballMaterial = ball.mesh.material as THREE.MeshBasicMaterial;
    const setBallCenteredCharged = () => {
      ball.mesh.position.set(0, ballSpawnY, 0);
      ball.velocity.set(0, 0, 0);
      ball.mesh.scale.setScalar(1.06);
      ballMaterial.color.setHex(0xdcc9ff);
    };
    const launchBallFromCenterSlow = () => {
      ball.mesh.position.set(0, ballSpawnY, 0);
      const xDirection = random() < 0.5 ? -1 : 1;
      const xSpeed = randomBetween(random, 0.055, 0.08) * xDirection;
      const ySpeed = randomBetween(random, -0.015, 0.045);
      ball.velocity.set(xSpeed, ySpeed, 0);
      ball.mesh.scale.setScalar(1);
      ballMaterial.color.setHex(0xffffff);
    };
    const beginRoundCountdown = () => {
      clearLocalInput();
      setBallCenteredCharged();
      roundCountdownEndAt = performance.now() + 4000;
      lastRoundCountdownText = '3';
      if (goalCelebrationUiShown) {
        goalCelebrationUiShown = false;
        setGoalCelebrationActive(false);
      }
      setRoundCountdownText('3');
    };
    const beginGoalCelebration = () => {
      clearLocalInput();
      setBallCenteredCharged();
      goalCelebrationEndAt = performance.now() + 1250;
      pendingRoundCountdownAfterGoal = true;
      lastRoundCountdownText = 'GOAL!';
      goalCelebrationUiShown = true;
      setGoalCelebrationActive(true);
      setRoundCountdownText('GOAL!');
      // Clear mystery boxes and pickups between rounds
      activeMysteryBoxes.forEach(b => b.cleanup());
      activeMysteryBoxes = [];
      activePowerupPickups.forEach(p => p.collect());
      activePowerupPickups = [];
      mysteryBoxNextSpawnAt = Date.now() + 10000;
      [player1, player2].forEach(p => {
        p.tkGrabFrames = -1;
        if (p.tkBeam) {
          scene.remove(p.tkBeam);
          p.tkBeam.geometry.dispose();
          (p.tkBeam.material as THREE.Material).dispose();
          p.tkBeam = null;
        }
      });
    };
    beginRoundCountdown();

    const beginPause = () => {
      if (
        matchEnded ||
        pausedForMenu ||
        performance.now() < countdownEndAt ||
        performance.now() < goalCelebrationEndAt ||
        performance.now() < roundCountdownEndAt
      ) {
        return;
      }
      pausedForMenu = true;
      clearLocalInput();
      setPauseMenuOpen(true);
    };

    const beginResumeCountdown = () => {
      if (matchEnded || !pausedForMenu) {
        return;
      }
      pausedForMenu = false;
      countdownEndAt = performance.now() + 3000;
      lastCountdownValue = 3;
      setPauseMenuOpen(false);
      setResumeCountdown(3);
    };

    resumeFromPauseRef.current = beginResumeCountdown;
    exitMatchRef.current = () => {
      setPauseMenuOpen(false);
      setResumeCountdown(null);
      onExit?.();
    };

    const mouse = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();
    const aimPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const worldAimPoint = new THREE.Vector3();

    const syncAimFromPointer = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hit = raycaster.ray.intersectPlane(aimPlane, worldAimPoint);
      if (hit) {
        activePlayer.setAimDirection(worldAimPoint);
        localInput.aimX = worldAimPoint.x;
        localInput.aimY = worldAimPoint.y;
      }
      return hit;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (pausedForMenu) {
          beginResumeCountdown();
        } else {
          beginPause();
        }
        return;
      }
      if (
        pausedForMenu ||
        performance.now() < countdownEndAt ||
        performance.now() < goalCelebrationEndAt ||
        performance.now() < roundCountdownEndAt ||
        matchEnded
      ) {
        return;
      }
      switch(e.key) {
        case 'a': localInput.left = true; break;
        case 'd': localInput.right = true; break;
        case 'w':
        case ' ':
          localInput.jumpQueued = true;
          localInput.jumpHeld = true;
          break;
        case 's': localInput.shieldHeld = true; break;
        case 'ArrowDown': localInput.down = true; break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (
        pausedForMenu ||
        performance.now() < countdownEndAt ||
        performance.now() < goalCelebrationEndAt ||
        performance.now() < roundCountdownEndAt ||
        matchEnded
      ) {
        return;
      }
      switch(e.key) {
        case 'a': localInput.left = false; break;
        case 'd': localInput.right = false; break;
        case 'w':
        case ' ':
          localInput.jumpHeld = false;
          break;
        case 's': localInput.shieldHeld = false; break;
        case 'ArrowDown': localInput.down = false; break;
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (
        pausedForMenu ||
        performance.now() < countdownEndAt ||
        performance.now() < goalCelebrationEndAt ||
        performance.now() < roundCountdownEndAt ||
        matchEnded
      ) {
        return;
      }
      syncAimFromPointer(e);
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (pausedForMenu || performance.now() < countdownEndAt || matchEnded) {
        return;
      }
      syncAimFromPointer(e);
      localInput.castQueued = true;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    renderer.domElement.addEventListener('pointermove', handlePointerMove);
    renderer.domElement.addEventListener('pointerdown', handlePointerDown);
    const applyRemoteFrame = (payload: {
      input: Pick<ControllerFrame, 'left' | 'right' | 'down' | 'jumpHeld' | 'shieldHeld' | 'aimX' | 'aimY'>;
      jumpSeq: number;
      castSeq: number;
      auth?: AuthorityState;
    }) => {
      remoteLastSeenAt = performance.now();
      remoteInput.left = payload.input.left;
      remoteInput.right = payload.input.right;
      remoteInput.down = payload.input.down;
      remoteInput.jumpHeld = payload.input.jumpHeld;
      remoteInput.shieldHeld = payload.input.shieldHeld;
      remoteInput.aimX = payload.input.aimX;
      remoteInput.aimY = payload.input.aimY;
      if (payload.jumpSeq > remoteJumpSeq) {
        remoteInput.jumpQueued = true;
        remoteJumpSeq = payload.jumpSeq;
      }
      if (payload.castSeq > remoteCastSeq) {
        remoteInput.castQueued = true;
        remoteCastSeq = payload.castSeq;
      }

      // ── Apply player1 authority state (only when we are player2) ──────────────
      if (localRole !== 'player2' || !payload.auth) return;
      const auth = payload.auth;

      // Ball sync: full snap to auth — local physics is continuously re-anchored each poll
      ball.mesh.position.x = auth.ball.x;
      ball.mesh.position.y = auth.ball.y;
      ball.velocity.x = auth.ball.vx;
      ball.velocity.y = auth.ball.vy;
      lastAuthBall = { x: auth.ball.x, y: auth.ball.y, vx: auth.ball.vx, vy: auth.ball.vy };
      lastAuthBallAt = performance.now();

      // Score sync: always authoritative — compare against local to detect goals/corrections
      const authP1 = Number(auth.score.p1) || 0;
      const authP2 = Number(auth.score.p2) || 0;
      const scoreIncreased = authP1 > player1ScoreLocal || authP2 > player2ScoreLocal;
      const scoreChanged = authP1 !== player1ScoreLocal || authP2 !== player2ScoreLocal;
      if (scoreChanged) {
        player1ScoreLocal = authP1;
        player2ScoreLocal = authP2;

        if (auth.matchEnded) {
          if (!matchEnded) {
            matchEnded = true;
            setRoundCountdownText(null);
            setGoalCelebrationActive(false);
            const w = auth.winner ?? 1;
            setGameState(prev => ({
              ...prev,
              player1Score: player1ScoreLocal,
              player2Score: player2ScoreLocal,
              gameStatus: 'ended',
              winner: w === 1 ? 'red' : 'blue',
            }));
            onMatchEnd?.({
              winner: w,
              p1Score: player1ScoreLocal,
              p2Score: player2ScoreLocal,
              isVsAi: false,
              player1Id: mergedConfig.player1Id || '',
              player2Id: mergedConfig.player2Id || '',
            });
          }
        } else {
          setGameState(prev => ({ ...prev, player1Score: player1ScoreLocal, player2Score: player2ScoreLocal }));
          if (scoreIncreased) beginGoalCelebration();
        }
      }

      // Mystery box sync: create/remove to match player1's list
      const authBoxIds = new Set(auth.boxes.map(b => b.id));
      activeMysteryBoxes = activeMysteryBoxes.filter(b => {
        if (!authBoxIds.has(b.id)) { b.cleanup(); return false; }
        return true;
      });
      const localBoxIds = new Set(activeMysteryBoxes.map(b => b.id));
      for (const ab of auth.boxes) {
        if (!localBoxIds.has(ab.id)) {
          activeMysteryBoxes.push(new MysteryBox(scene, ab.x, ab.y, ab.id));
        }
      }

      // Powerup pickup sync: create/remove to match player1's list
      const authPickupIds = new Set(auth.pickups.map(p => p.id));
      activePowerupPickups = activePowerupPickups.filter(p => {
        if (!authPickupIds.has(p.id)) { p.collect(); return false; }
        return true;
      });
      const localPickupIds = new Set(activePowerupPickups.map(p => p.id));
      for (const ap of auth.pickups) {
        if (!localPickupIds.has(ap.id)) {
          activePowerupPickups.push(
            new PowerupPickup(scene, new THREE.Vector3(ap.x, ap.y, 0), ap.type as PowerupType, ap.id)
          );
        }
      }
    };

    const dislodgeBallFromPlayers = (extraKick = 0.04) => {
      const contactRadius = 0.84;
      [player1, player2].forEach((player) => {
        const offset = new THREE.Vector3().subVectors(ball.mesh.position, player.mesh.position);
        const distance = offset.length();
        if (distance >= contactRadius) {
          return;
        }
        const normal = distance > 0.0001
          ? offset.multiplyScalar(1 / distance)
          : new THREE.Vector3(player.id === 'player1' ? 1 : -1, 0.2, 0).normalize();
        const overlap = contactRadius - Math.max(distance, 0.0001);
        ball.mesh.position.addScaledVector(normal, overlap + 0.018);
        ball.velocity.addScaledVector(normal, extraKick);
      });
    };

    const pushBallByExplosion = (spell: Spell) => {
      if (!spell.explosionMesh || spell.ballImpulseApplied) {
        return;
      }
      const effectiveRadius = spell.explosionRadius + 0.35;
      const distance = ball.mesh.position.distanceTo(spell.explosionMesh.position);
      if (distance >= effectiveRadius) {
        return;
      }
      const rawDirection = new THREE.Vector3().subVectors(ball.mesh.position, spell.explosionMesh.position);
      if (rawDirection.lengthSq() < 0.0001) {
        rawDirection.set(spell.owner === 'player1' ? 1 : -1, 0.3, 0);
      } else {
        rawDirection.normalize();
      }
      const centeredness = 1 - (distance / effectiveRadius);
      const impulseStrength = 0.16 + centeredness * 0.26;
      if (centeredness > 0.92) {
        rawDirection.set(spell.owner === 'player1' ? 1 : -1, 0.05, 0);
      }
      // Always give some lift so grounded hits arc away instead of dragging.
      rawDirection.y = Math.max(rawDirection.y, 0.22);
      rawDirection.normalize();
      ball.applyImpulse(rawDirection.multiplyScalar(impulseStrength));
      // Ensure immediate floor release if blast happens while grounded.
      if (ball.mesh.position.y <= ball.radius + 0.01) {
        ball.mesh.position.y = ball.radius + 0.02;
        ball.velocity.y = Math.max(ball.velocity.y, 0.2);
      }
      dislodgeBallFromPlayers(0.08);
      spell.ballImpulseApplied = true;
    };

    const pushPlayerBySpell = (spell: Spell, target: Player, directHit: boolean) => {
      const origin = spell.explosionMesh?.position ?? spell.mesh.position;
      const direction = new THREE.Vector3().subVectors(target.mesh.position, origin);
      if (direction.lengthSq() < 0.0001) {
        direction.set(spell.owner === 'player1' ? 1 : -1, 0.6, 0);
      }
      direction.y = Math.max(direction.y, directHit ? 0.45 : 0.2);
      direction.normalize();

      const shieldFactor = target.shieldActive ? 0.32 : 1;
      const baseStrength = directHit ? 0.22 : 0.075;
      // Attacker's RPS multiplier boosts knockback dealt
      const attacker = spell.owner === 'player1' ? player1 : player2;
      const rpsFactor = attacker.rpsMultiplier;
      target.applyKnockback(direction.multiplyScalar(baseStrength * shieldFactor * rpsFactor));
      spell.nudgedPlayers.add(target.id);
    };

    let localJumpSeq = 0;
    let localCastSeq = 0;

    const applyControllerToPlayer = (player: Player, frame: ControllerFrame) => {
      player.pressingDown = frame.down;
      player.setJumpHeld(frame.jumpHeld);
      player.setShieldHeld(frame.shieldHeld);
      if (frame.left) {
        player.moveLeft();
      }
      if (frame.right) {
        player.moveRight();
      }
      if (frame.jumpQueued) {
        player.queueJump();
        frame.jumpQueued = false;
      }
      player.setAimDirection(new THREE.Vector3(frame.aimX, frame.aimY, 0));
      if (frame.castQueued) {
        if (!player.isFlying()) {
          const target = new THREE.Vector3(frame.aimX, frame.aimY, 0);
          if (player.hasPowerup('telekinesis') && player.tkGrabFrames < 0 && player.tkCooldown <= 0) {
            player.tkGrabFrames = 60;
            player.tkCooldown = 90;
          } else if (player.hasPowerup('teleport')) {
            if (player.spellCooldown <= 0) {
              player.mesh.position.set(frame.aimX, Math.max(frame.aimY, 0.44), 0);
              player.velocity.set(0, 0, 0);
              player.spellCooldown = 22;
            }
          } else if (player.tkGrabFrames < 0) {
            const homingTargets = player.hasPowerup('homingShots')
              ? [{ position: ball.mesh.position }, { position: (player.id === 'player1' ? player2 : player1).mesh.position }]
              : undefined;
            const spells = player.castSpells(scene, target, homingTargets);
            spells.forEach(s => activeSpells.push(s));
          }
        }
        frame.castQueued = false;
      }
    };

    const updateAiController = (aiPlayer: Player, frame: ControllerFrame) => {
      const aiDifficulty = mergedConfig.aiDifficulty ?? 3;
      // linearSkill: 0 at diff=1, 1 at diff=10
      const linearSkill = THREE.MathUtils.clamp((aiDifficulty - 1) / 9, 0, 1);
      // Aggressive exponential curve: lv1-3 are vegetables, lv7+ are gods.
      // pow(x, 3) means lv5 (linearSkill=0.44) → pace=0.087, lv8 (0.78)→0.47, lv10→1.0
      const pace = Math.pow(linearSkill, 3);
      // For high-level (8-10) specific overrides we use a separate "elite" factor
      const elite = THREE.MathUtils.clamp((aiDifficulty - 7) / 3, 0, 1); // 0 below lv7, 1 at lv10
      const lerp = (slow: number, fast: number) => THREE.MathUtils.lerp(slow, fast, pace);
      const eliteLerp = (slow: number, fast: number) => THREE.MathUtils.lerp(slow, fast, elite);

      // ── Reaction ─────────────────────────────────────────────────────────────
      // Lv1: 4-7 sec gaps. Lv5: ~2 sec. Lv8+: every single frame.
      const reactionMin      = elite > 0.3 ? 0 : Math.round(lerp(240, 8));
      const reactionVariance = elite > 0.3 ? 1 : Math.round(lerp(180, 4));

      // ── Aim & prediction ──────────────────────────────────────────────────────
      const aimErrorX    = lerp(12.0, 0.02);
      const aimErrorY    = lerp(6.0,  0.02);
      const prediction   = lerp(0.0,  40.0);
      const lateralNoise = lerp(8.0,  0.05);

      // ── Movement ──────────────────────────────────────────────────────────────
      const deadZone         = lerp(8.0,  0.08);
      const hesitationChance = lerp(0.95, 0.0);
      const wrongDirChance   = lerp(0.4,  0.0);

      // ── Jumping ───────────────────────────────────────────────────────────────
      const jumpChance    = lerp(0.005, 0.98);
      const jumpBallDelta = lerp(4.0,   0.2);
      frame.jumpHeld = aiPlayer.velocity.y > 0.05 && random() < lerp(0.01, 0.92);

      // ── Casting ───────────────────────────────────────────────────────────────
      const castChance      = lerp(0.001, 0.7);
      const castRange       = lerp(1.2,   25.0);
      const lineupRange     = lerp(0.6,   25.0);
      const castCooldownMin = elite > 0.5 ? 8 : Math.round(lerp(900, 20));
      const castCooldownVar = Math.round(lerp(500, 2));

      // ── Shield ────────────────────────────────────────────────────────────────
      const shieldChance = lerp(0.0, 0.7);
      const shieldRange  = lerp(1.0, 9.0);

      // ── Elite: goal defense positioning (lv8+) ─────────────────────────────
      // High-level AI doesn't just chase ball — it positions between ball and own goal.
      const goalX = aiPlayer.id === 'player1' ? -worldHalfWidth + 1 : worldHalfWidth - 1;

      // ── Elite AI runs EVERY FRAME with zero reaction delay ──────────────────
      const isElite = elite > 0.3;

      if (!isElite && aiReactionFrames > 0) {
        aiReactionFrames -= 1;
      } else {
        // Predict ball position several frames ahead
        const predictedBallX = ball.mesh.position.x + ball.velocity.x * prediction;
        const predictedBallY = ball.mesh.position.y + ball.velocity.y * prediction;

        // Elite AI: position between ball and own goal for defense
        let targetX: number;
        if (isElite) {
          // Intercept point: bias toward where ball is going, weighted toward own goal defense
          const interceptX = predictedBallX;
          const defenseX = (goalX + interceptX) * 0.5; // midpoint between goal and ball
          // Ball heading toward our goal? Defend harder. Ball heading away? Chase it.
          const ballHeadingToGoal = aiPlayer.id === 'player1'
            ? ball.velocity.x < -0.01
            : ball.velocity.x > 0.01;
          targetX = ballHeadingToGoal
            ? THREE.MathUtils.lerp(interceptX, defenseX, eliteLerp(0.3, 0.6))
            : interceptX;
        } else {
          targetX = predictedBallX + randomBetween(random, -lateralNoise, lateralNoise);
        }

        const deltaX = targetX - aiPlayer.mesh.position.x;

        frame.left  = deltaX < -deadZone;
        frame.right = deltaX > deadZone;

        // Low-level hesitation
        if (!isElite && random() < hesitationChance) {
          frame.left = false;
          frame.right = false;
        }
        if (!isElite && random() < wrongDirChance) {
          const tmp = frame.left;
          frame.left = frame.right;
          frame.right = tmp;
        }

        frame.down = false;

        // Shield: elite always shields incoming spells
        const spellIncoming = activeSpells.some(s =>
          s.owner !== aiPlayer.id &&
          s.mesh.position.distanceTo(aiPlayer.mesh.position) < shieldRange
        );
        frame.shieldHeld = spellIncoming
          ? random() < (isElite ? 0.98 : lerp(0.02, 0.8))
          : random() < shieldChance && ball.mesh.position.distanceTo(aiPlayer.mesh.position) < shieldRange;

        // Aim
        const errX = isElite ? 0 : randomBetween(random, -aimErrorX, aimErrorX);
        const errY = isElite ? 0 : randomBetween(random, -aimErrorY, aimErrorY);
        frame.aimX = predictedBallX + errX;
        frame.aimY = predictedBallY + 0.1 + errY;

        // Jump
        const ballHighEnough = predictedBallY > aiPlayer.mesh.position.y + jumpBallDelta;
        const horizontallyClose = Math.abs(deltaX) < (isElite ? 6 : lerp(7.0, 4.0));
        if (aiPlayer.onGround && ballHighEnough && horizontallyClose && random() < jumpChance) {
          frame.jumpQueued = true;
        }
        // Elite: aggressively double-jump to reach aerial balls
        if (!aiPlayer.onGround && isElite && predictedBallY > aiPlayer.mesh.position.y + 0.3) {
          frame.jumpQueued = true;
        }

        if (!isElite) {
          aiReactionFrames = reactionMin + Math.floor(random() * Math.max(1, reactionVariance));
        }
      }

      // ── Casting (runs every frame, gated by its own cooldown) ─────────────────
      if (aiCastCooldown > 0) {
        aiCastCooldown -= 1;
      }
      const castDistance = aiPlayer.mesh.position.distanceTo(ball.mesh.position);
      const hasLineup    = Math.abs(ball.mesh.position.x - aiPlayer.mesh.position.x) < lineupRange;
      if (aiCastCooldown === 0 && castDistance < castRange && hasLineup && random() < castChance) {
        frame.castQueued = true;
        aiCastCooldown = castCooldownMin + Math.floor(random() * Math.max(1, castCooldownVar));
      }
    };

    const animate = () => {
      frameHandle = requestAnimationFrame(animate);
      if (matchEnded) {
        renderer.render(scene, camera);
        return;
      }

      if (pausedForMenu) {
        renderer.render(scene, camera);
        return;
      }

      const remainingCountdownMs = countdownEndAt - performance.now();
      if (remainingCountdownMs > 0) {
        const nextCountdown = Math.ceil(remainingCountdownMs / 1000);
        if (nextCountdown !== lastCountdownValue) {
          lastCountdownValue = nextCountdown;
          setResumeCountdown(nextCountdown);
        }
        renderer.render(scene, camera);
        return;
      }
      if (lastCountdownValue !== null) {
        lastCountdownValue = null;
        setResumeCountdown(null);
      }

      const remainingGoalMs = goalCelebrationEndAt - performance.now();
      if (remainingGoalMs > 0) {
        if (!goalCelebrationUiShown) {
          goalCelebrationUiShown = true;
          setGoalCelebrationActive(true);
        }
        if (lastRoundCountdownText !== 'GOAL!') {
          lastRoundCountdownText = 'GOAL!';
          setRoundCountdownText('GOAL!');
        }
        renderer.render(scene, camera);
        return;
      }
      if (pendingRoundCountdownAfterGoal) {
        pendingRoundCountdownAfterGoal = false;
        beginRoundCountdown();
        renderer.render(scene, camera);
        return;
      }

      const remainingRoundMs = roundCountdownEndAt - performance.now();
      if (remainingRoundMs > 0) {
        const nextRoundText = remainingRoundMs > 3000
          ? '3'
          : remainingRoundMs > 2000
            ? '2'
            : remainingRoundMs > 1000
              ? '1'
              : 'BATTLE!';
        if (nextRoundText !== lastRoundCountdownText) {
          lastRoundCountdownText = nextRoundText;
          setRoundCountdownText(nextRoundText);
        }
        const chargePulse = 1.04 + Math.sin(performance.now() * 0.02) * 0.05;
        ball.mesh.scale.setScalar(chargePulse);
        renderer.render(scene, camera);
        return;
      }
      if (lastRoundCountdownText) {
        lastRoundCountdownText = '';
        setRoundCountdownText(null);
        launchBallFromCenterSlow();
      }

      if (mergedConfig.parallax) {
        // Movement-relative parallax prevents wrap-around snapbacks.
        const focusX = (activePlayer.mesh.position.x * 0.22) + (controlledByAi.mesh.position.x * 0.18) + (ball.mesh.position.x * 0.6);
        const deltaFocusX = shortestWrappedDelta(focusX, parallaxFocusX, horizontalWrapSpan);
        parallaxFocusX = focusX;
        parallaxPhaseX += deltaFocusX;
        parallaxSmoothX = THREE.MathUtils.lerp(parallaxSmoothX, parallaxPhaseX, 0.12);

        const deltaBallY = ball.mesh.position.y - lastBallY;
        lastBallY = ball.mesh.position.y;
        parallaxPhaseY += deltaBallY;
        parallaxSmoothY = THREE.MathUtils.lerp(parallaxSmoothY, parallaxPhaseY, 0.08);

        parallaxLayers.far.material.map!.offset.x = parallaxSmoothX * 0.0044;
        parallaxLayers.far.material.map!.offset.y = parallaxSmoothY * 0.0012;
        if (parallaxLayers.mid?.material.map) {
          parallaxLayers.mid.material.map.offset.x = parallaxSmoothX * 0.0054;
          parallaxLayers.mid.material.map.offset.y = parallaxSmoothY * 0.0015;
        }
        if (parallaxLayers.near?.material.map) {
          parallaxLayers.near.material.map.offset.x = parallaxSmoothX * 0.0062;
          parallaxLayers.near.material.map.offset.y = parallaxSmoothY * 0.0018;
        }
        if (parallaxLayers.edgeFog) {
          parallaxLayers.edgeFog.position.x = parallaxSmoothX * 0.01;
        }
      }

      const time = performance.now() * 0.001;
      floorMaterial.uniforms.uTime.value = time;
      leftWall.updateBounds(time);
      rightWall.updateBounds(time + Math.PI);

      portals.forEach((portal, idx) => {
        const pulse = 0.5 + 0.5 * Math.sin(time * 3.2 + portal.pulseOffset);
        const slowPulse = 0.5 + 0.5 * Math.sin(time * 1.35 + portal.pulseOffset + idx);
        portal.ring.rotation.z += 0.006;
        portal.rim.rotation.z -= 0.01;
        portal.inner.rotation.z += 0.014;
        portal.inner.material.opacity = 0.14 + pulse * 0.24;
        portal.glow.material.opacity = 0.2 + pulse * 0.22;
        portal.fog.material.opacity = 0.06 + slowPulse * 0.12;
        portal.glow.scale.set(1.85 + pulse * 0.3, 2.2 + pulse * 0.35, 1);
        portal.fog.scale.set(2.5 + slowPulse * 0.28, 2.85 + slowPulse * 0.34, 1);
      });

      if (mobileControlsEnabled) {
        const moveStick = moveStickRef.current;
        const aimStick = aimStickRef.current;
        localInput.left = moveStick.x < -0.12;
        localInput.right = moveStick.x > 0.12;
        localInput.down = moveStick.y > 0.5;

        const jumpIntent = moveStick.active && moveStick.y < -0.55;
        if (jumpIntent && !moveStick.jumpLatch) {
          localInput.jumpQueued = true;
          moveStick.jumpLatch = true;
        } else if (!jumpIntent) {
          moveStick.jumpLatch = false;
        }
        localInput.jumpHeld = jumpIntent;
        localInput.shieldHeld = shieldTouchRef.current;

        if (aimStick.active) {
          localInput.aimX = activePlayer.mesh.position.x + aimStick.x * 4.8;
          localInput.aimY = activePlayer.mesh.position.y + aimStick.y * 3.2;
        }
        if (aimStick.fireQueued) {
          localInput.castQueued = true;
          aimStick.fireQueued = false;
        }
      }

      if (localInput.jumpQueued) {
        localJumpSeq += 1;
      }
      if (localInput.castQueued) {
        localCastSeq += 1;
      }

      applyControllerToPlayer(activePlayer, localInput);

      const nowMs = performance.now();
      const remoteConnected = mergedConfig.mode === 'matchmaking' && nowMs - remoteLastSeenAt < REMOTE_STALE_MS;
      const isP2Online = mergedConfig.mode === 'matchmaking' && localRole === 'player2';
      if (mergedConfig.mode === 'matchmaking') {
        const roomCode = (mergedConfig.matchmakingRoom ?? '').trim().toUpperCase();
        const shouldSend = roomCode && nowMs - lastNetworkSendAt > 50 && !networkSendBusy;
        const shouldPoll = roomCode && nowMs - lastNetworkPollAt > 60 && !networkPollBusy;

        if (shouldSend) {
          networkSendBusy = true;
          lastNetworkSendAt = nowMs;
          const payload: Record<string, unknown> = {
            room: roomCode,
            player: localRole,
            input: {
              left: localInput.left,
              right: localInput.right,
              down: localInput.down,
              jumpHeld: localInput.jumpHeld,
              shieldHeld: localInput.shieldHeld,
              aimX: localInput.aimX,
              aimY: localInput.aimY
            },
            jumpSeq: localJumpSeq,
            castSeq: localCastSeq,
            sentAt: Date.now()
          };
          // Player1 is the authority for ball / score / items — broadcast state every frame
          if (localRole === 'player1') {
            payload.auth = {
              ball: {
                x: ball.mesh.position.x,
                y: ball.mesh.position.y,
                vx: ball.velocity.x,
                vy: ball.velocity.y,
              },
              score: { p1: player1ScoreLocal, p2: player2ScoreLocal },
              celebrating: goalCelebrationEndAt > nowMs || roundCountdownEndAt > nowMs || pendingRoundCountdownAfterGoal,
              matchEnded,
              winner: matchEnded ? (player1ScoreLocal >= WIN_SCORE ? 1 : 2) : null,
              boxes: activeMysteryBoxes.map(b => ({ id: b.id, x: b.group.position.x, y: b.group.position.y })),
              pickups: activePowerupPickups.map(p => ({ id: p.id, x: p.mesh.position.x, y: p.mesh.position.y, type: p.type })),
            };
          }
          fetch('/api/match/control', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }).catch(() => {
            // Swallow network hiccups; AI fallback handles gaps.
          }).finally(() => {
            networkSendBusy = false;
          });
        }

        if (shouldPoll) {
          networkPollBusy = true;
          lastNetworkPollAt = nowMs;
          fetch(`/api/match/state?room=${encodeURIComponent(roomCode)}`)
            .then((response) => response.ok ? response.json() : null)
            .then((payload: {
              player1: null | { input: ControllerFrame; jumpSeq: number; castSeq: number; auth?: AuthorityState };
              player2: null | { input: ControllerFrame; jumpSeq: number; castSeq: number };
            } | null) => {
              if (!payload) {
                return;
              }
              const remoteRole = localRole === 'player1' ? 'player2' : 'player1';
              const remotePayload = remoteRole === 'player1' ? payload.player1 : payload.player2;
              if (remotePayload) {
                applyRemoteFrame({
                  input: {
                    left: Boolean(remotePayload.input.left),
                    right: Boolean(remotePayload.input.right),
                    down: Boolean(remotePayload.input.down),
                    jumpHeld: Boolean(remotePayload.input.jumpHeld),
                    shieldHeld: Boolean(remotePayload.input.shieldHeld),
                    aimX: Number(remotePayload.input.aimX) || 0,
                    aimY: Number(remotePayload.input.aimY) || 0
                  },
                  jumpSeq: Number(remotePayload.jumpSeq) || 0,
                  castSeq: Number(remotePayload.castSeq) || 0,
                  auth: (remotePayload as { auth?: AuthorityState }).auth,
                });
              }
            })
            .catch(() => {
              // Keep running locally with AI fallback on network failures.
            })
            .finally(() => {
              networkPollBusy = false;
            });
        }
      }

      if (mergedConfig.mode === 'matchmaking' && remoteConnected) {
        applyControllerToPlayer(controlledByAi, remoteInput);
      } else {
        updateAiController(controlledByAi, remoteInput);
        applyControllerToPlayer(controlledByAi, remoteInput);
      }

      const nextStatus = mergedConfig.mode === 'matchmaking'
        ? (remoteConnected ? `Online Match ${mergedConfig.matchmakingRoom || ''}` : `Waiting In ${mergedConfig.matchmakingRoom || ''} + AI`)
        : 'Offline AI';
      if (nextStatus !== lastConnectionLabel) {
        lastConnectionLabel = nextStatus;
        setConnectionStatus(nextStatus);
      }

      player1.update(platforms, worldHalfWidth);
      player2.update(platforms, worldHalfWidth);
      ball.update(gameHeight, worldHalfWidth, platforms);

      // Per-frame ball correction toward auth extrapolation (player2 online only).
      // Runs every frame — not just on poll — so drift is squeezed out continuously
      // rather than waiting ~120ms for the next network event.
      if (isP2Online && lastAuthBall !== null) {
        const ageMs = nowMs - lastAuthBallAt;
        if (ageMs < 300) {
          const dt = ageMs / 1000;
          // Linear extrapolation from last-known auth velocity
          const targetX = lastAuthBall.x + lastAuthBall.vx * dt;
          const targetY = lastAuthBall.y + lastAuthBall.vy * dt;
          const PULL = 0.4;
          ball.mesh.position.x += (targetX - ball.mesh.position.x) * PULL;
          ball.mesh.position.y += (targetY - ball.mesh.position.y) * PULL;
          ball.velocity.x += (lastAuthBall.vx - ball.velocity.x) * PULL;
          ball.velocity.y += (lastAuthBall.vy - ball.velocity.y) * PULL;
        }
      }

      [player1, player2].forEach((player) => {
        const frame = player === activePlayer ? localInput : remoteInput;
        if (player.tkGrabFrames > 0) {
          player.tkGrabFrames -= 1;
          const aimTarget = new THREE.Vector3(frame.aimX, frame.aimY, 0);
          const toBall = new THREE.Vector3().subVectors(aimTarget, ball.mesh.position);
          const pullStrength = 0.12;
          ball.velocity.x += toBall.x * pullStrength;
          ball.velocity.y += toBall.y * pullStrength;
          ball.velocity.x *= 0.88;
          ball.velocity.y *= 0.88;

          if (!player.tkBeam) {
            const beamGeo = new THREE.BufferGeometry().setFromPoints([
              new THREE.Vector3(), new THREE.Vector3()
            ]);
            const beamMat = new THREE.LineBasicMaterial({
              color: 0x14b8a6,
              transparent: true,
              opacity: 0.7,
              linewidth: 2
            });
            player.tkBeam = new THREE.Line(beamGeo, beamMat);
            scene.add(player.tkBeam);
          }
          const positions = player.tkBeam.geometry.attributes.position as THREE.BufferAttribute;
          positions.setXYZ(0, player.mesh.position.x, player.mesh.position.y + 0.5, 0.1);
          positions.setXYZ(1, ball.mesh.position.x, ball.mesh.position.y, 0.1);
          positions.needsUpdate = true;
          const fade = player.tkGrabFrames / 60;
          (player.tkBeam.material as THREE.LineBasicMaterial).opacity = 0.7 * fade;
        } else if (player.tkBeam) {
          scene.remove(player.tkBeam);
          player.tkBeam.geometry.dispose();
          (player.tkBeam.material as THREE.Material).dispose();
          player.tkBeam = null;
        }
      });

      activeSpells = activeSpells.filter((spell) => !spell.update(platforms, ball, worldHalfWidth));

      const chainSpawns: Spell[] = [];
      activeSpells.forEach(spell => {
        if (spell.exploded && spell.pendingChains.length > 0) {
          chainSpawns.push(...spell.tickChains());
        }
      });
      chainSpawns.forEach(s => activeSpells.push(s));

      // Spells can directly hit players and cause strong knockback.
      activeSpells.forEach((spell) => {
        if (spell.exploded) {
          return;
        }
        [player1, player2].forEach((target) => {
          if (target.id === spell.owner) {
            return;
          }
          const toTarget = target.mesh.position.distanceTo(spell.mesh.position);
          const shieldRadius = target.shieldActive ? 0.8 : 0;
          if (target.shieldActive && toTarget <= shieldRadius) {
            spell.explode();
            pushPlayerBySpell(spell, target, false);
            return;
          }
          if (toTarget <= 0.52) {
            spell.explode();
            pushPlayerBySpell(spell, target, true);
          }
        });
      });

      [player1, player2].forEach((player) => {
        const contactRadius = 0.84;
        const playerSupportRadius = 0.44;
        const offset = new THREE.Vector3().subVectors(ball.mesh.position, player.mesh.position);
        const distance = offset.length();
        if (distance >= contactRadius) {
          return;
        }

        const normal = distance > 0.0001
          ? offset.multiplyScalar(1 / distance)
          : new THREE.Vector3(player.id === 'player1' ? 1 : -1, 0.2, 0).normalize();
        const overlap = contactRadius - Math.max(distance, 0.0001);

        // Always separate penetration first to avoid jitter loops.
        ball.mesh.position.addScaledVector(normal, overlap + 0.012);

        const playerIsAboveBall = player.mesh.position.y > ball.mesh.position.y + 0.2;
        const playerFallingOntoBall = player.velocity.y <= 0.03;
        if (playerIsAboveBall && playerFallingOntoBall) {
          // Allow controlled "stand/jump on ball" behavior without interpenetration.
          const desiredY = ball.mesh.position.y + ball.radius + playerSupportRadius;
          if (player.mesh.position.y < desiredY + 0.12) {
            player.mesh.position.y = desiredY;
            player.velocity.y = Math.max(player.velocity.y, 0);
            player.onGround = true;
          }
          ball.velocity.x += player.velocity.x * 0.42;
          ball.velocity.y = Math.min(ball.velocity.y, -0.045);
        } else {
          ball.bounce(normal, 1.03);
          ball.velocity.addScaledVector(normal, ball.velocity.length() < 0.08 ? 0.06 : 0.02);
        }
      });

      activeSpells.forEach((spell) => pushBallByExplosion(spell));
      activeSpells.forEach((spell) => {
        if (!spell.explosionMesh) {
          return;
        }
        [player1, player2].forEach((target) => {
          if (target.id === spell.owner || spell.nudgedPlayers.has(target.id)) {
            return;
          }
          const distance = target.mesh.position.distanceTo(spell.explosionMesh!.position);
          if (distance < spell.explosionRadius) {
            pushPlayerBySpell(spell, target, false);
          }
        });
      });

      // ── Mystery box spawn (player1 / offline only — player2 syncs from auth) ──
      const now = Date.now();
      if (!isP2Online && !matchEnded && !pausedForMenu && activeMysteryBoxes.length === 0 && activePowerupPickups.length === 0 && now >= mysteryBoxNextSpawnAt) {
        const bx = (Math.random() - 0.5) * (worldHalfWidth * 1.1);
        const by = 1.8 + Math.random() * 3.2;
        activeMysteryBoxes.push(new MysteryBox(scene, bx, by, boxIdCounter++));
        mysteryBoxNextSpawnAt = now + (20000 + Math.random() * 20000);
      }

      // ── Mystery box update + blast/player open ─────────────────────────────
      activeMysteryBoxes = activeMysteryBoxes.filter(box => {
        if (box.opened) return false;
        box.update(1 / 60);

        // player2 online: auth controls box lifecycle — no local interactions
        if (isP2Online) return true;

        // Check explosion hits
        let hit = false;
        for (const spell of activeSpells) {
          if (spell.exploded && spell.explosionMesh) {
            const d = spell.explosionMesh.position.distanceTo(box.group.position);
            if (d < spell.explosionRadius + 0.5) { hit = true; break; }
          }
          if (!spell.exploded) {
            const d = spell.mesh.position.distanceTo(box.group.position);
            if (d < 0.6) { hit = true; break; }
          }
        }
        // Check player melee hit
        for (const p of [player1, player2]) {
          if (p.mesh.position.distanceTo(box.group.position) < 0.8) { hit = true; break; }
        }

        if (hit) {
          const boxPos = box.group.position.clone();
          box.open();
          const randomType = ALL_POWERUPS[Math.floor(Math.random() * ALL_POWERUPS.length)];
          activePowerupPickups.push(new PowerupPickup(scene, boxPos, randomType, pickupIdCounter++));
          return false;
        }
        return true;
      });

      // ── Powerup pickup update + collection ────────────────────────────────
      activePowerupPickups = activePowerupPickups.filter(pickup => {
        if (pickup.collected) return false;
        pickup.update(1 / 60);
        // player2 online: auth controls pickup lifecycle — no local collections or expiry
        if (isP2Online) return true;
        for (const p of [player1, player2]) {
          if (p.mesh.position.distanceTo(pickup.mesh.position) < 0.62) {
            pickup.collect();
            p.grantPowerup(pickup.type);
            return false;
          }
        }
        // auto-expire after 20s
        if (pickup.age > 20 * 60) { pickup.collect(); return false; }
        return true;
      });

      const ballX = ball.mesh.position.x;
      const ballY = ball.mesh.position.y;

      // Goal detection — player2 in online matchmaking defers entirely to player1's auth state
      if (!isP2Online) {
        if (ballX < -goalTriggerX && ballY > 1.45 && ballY < 3.45) {
          player2ScoreLocal += 1;
          if (player2ScoreLocal >= WIN_SCORE) {
            matchEnded = true;
            setRoundCountdownText(null);
            setGoalCelebrationActive(false);
            setGameState((prev) => ({ ...prev, player2Score: player2ScoreLocal, gameStatus: 'ended', winner: 'blue' }));
            onMatchEnd?.({ winner: 2, p1Score: player1ScoreLocal, p2Score: player2ScoreLocal, isVsAi: mergedConfig.mode !== 'matchmaking', player1Id: mergedConfig.player1Id || '', player2Id: mergedConfig.player2Id || '' });
          } else {
            setGameState((prev) => ({ ...prev, player2Score: player2ScoreLocal }));
            beginGoalCelebration();
          }
          return;
        }

        if (ballX > goalTriggerX && ballY > 1.45 && ballY < 3.45) {
          player1ScoreLocal += 1;
          if (player1ScoreLocal >= WIN_SCORE) {
            matchEnded = true;
            setRoundCountdownText(null);
            setGoalCelebrationActive(false);
            setGameState((prev) => ({ ...prev, player1Score: player1ScoreLocal, gameStatus: 'ended', winner: 'red' }));
            onMatchEnd?.({ winner: 1, p1Score: player1ScoreLocal, p2Score: player2ScoreLocal, isVsAi: mergedConfig.mode !== 'matchmaking', player1Id: mergedConfig.player1Id || '', player2Id: mergedConfig.player2Id || '' });
          } else {
            setGameState((prev) => ({ ...prev, player1Score: player1ScoreLocal }));
            beginGoalCelebration();
          }
          return;
        }
      }

      // Update local player powerup HUD ~6× per second
      if (Math.round(performance.now() / 16) % 10 === 0) {
        const pups: Partial<Record<PowerupType, number>> = {};
        for (const k of ALL_POWERUPS) {
          const v = activePlayer.powerups[k] ?? 0;
          if (v > 0) pups[k] = v;
        }
        setLocalPowerups(pups);
      }

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      sizeRenderer();
      applyResponsiveLayout();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameHandle);
      activeSpells.forEach((spell) => spell.cleanup());
      activeMysteryBoxes.forEach(b => b.cleanup());
      activePowerupPickups.forEach(p => p.collect());
      [player1, player2].forEach(p => {
        if (p.tkBeam) {
          scene.remove(p.tkBeam);
          p.tkBeam.geometry.dispose();
          (p.tkBeam.material as THREE.Material).dispose();
          p.tkBeam = null;
        }
      });
      currentMount?.removeChild(renderer.domElement);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('pointermove', handlePointerMove);
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      portals.forEach((portal) => {
        scene.remove(portal.group);
        portal.ring.geometry.dispose();
        portal.ring.material.dispose();
        portal.rim.geometry.dispose();
        portal.rim.material.dispose();
        portal.inner.geometry.dispose();
        portal.inner.material.dispose();
        portal.glow.material.dispose();
        portal.fog.material.dispose();
      });
      floor.geometry.dispose();
      floor.material.dispose();
      portalFogTexture.dispose();
      texture.dispose();
      parallaxLayers.far.material.dispose();
      parallaxLayers.far.geometry.dispose();
      if (parallaxLayers.mid) {
        parallaxLayers.mid.material.map?.dispose();
        parallaxLayers.mid.material.dispose();
        parallaxLayers.mid.geometry.dispose();
      }
      if (parallaxLayers.near) {
        parallaxLayers.near.material.map?.dispose();
        parallaxLayers.near.material.dispose();
        parallaxLayers.near.geometry.dispose();
      }
      if (parallaxLayers.edgeFog) {
        parallaxLayers.edgeFog.material.dispose();
        parallaxLayers.edgeFog.geometry.dispose();
      }
      renderer.dispose();
    };
  }, [mergedConfig, onExit, onMatchEnd, mobileControlsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const leftTeamColor = WIZARD_COLORS[mergedConfig.player1Color ?? 'cyan'];
  const rightTeamColor = WIZARD_COLORS[mergedConfig.player2Color ?? 'lavender'];

  return (
    <div className="w-full h-screen touch-none">
      <div ref={mountRef} className="w-full h-full touch-none" />
      {/* Score HUD */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 pointer-events-none">
        <div className="bg-slate-950/30 backdrop-blur-sm text-white px-4 py-2 rounded-md border border-white/20 shadow-[0_0_16px_rgba(15,23,42,0.3)]">
          <div className="flex items-center gap-3 text-3xl font-black leading-none">
            <span style={{ color: hexToCss(leftTeamColor.hex), textShadow: `0 0 10px ${hexToCss(leftTeamColor.hex)}AA` }}>{gameState.player1Score}</span>
            <span className="text-slate-300/80 text-lg">-</span>
            <span style={{ color: hexToCss(rightTeamColor.hex), textShadow: `0 0 10px ${hexToCss(rightTeamColor.hex)}AA` }}>{gameState.player2Score}</span>
          </div>
        </div>
      </div>

      {/* Local player powerup rings — only visible to the local player */}
      {gameState.gameStatus === 'playing' && !pauseMenuOpen && (
        <div
          className={`absolute left-1/2 -translate-x-1/2 flex gap-1.5 items-center pointer-events-none ${mobileControlsEnabled ? 'bottom-40' : 'bottom-4'}`}
        >
          {ALL_POWERUPS.filter(t => (localPowerups[t] ?? 0) > 0).map(type => (
            <PowerupBadge key={type} type={type} framesLeft={localPowerups[type]!} />
          ))}
        </div>
      )}
      {mobileControlsEnabled && gameState.gameStatus !== 'ended' && !pauseMenuOpen && resumeCountdown === null && roundCountdownText === null ? (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-5 bottom-6 pointer-events-auto">
            <div className="text-[10px] text-white/70 uppercase tracking-wide mb-2">Move</div>
            <div
              ref={leftJoystickRef}
              className="relative h-28 w-28 rounded-full border border-white/30 bg-slate-900/35 backdrop-blur-sm"
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => {
                e.preventDefault();
                updateStickFromPointer(e, leftJoystickRef, moveStickRef, setMoveStickUi);
              }}
              onPointerMove={(e) => {
                e.preventDefault();
                if (!moveStickRef.current.active) {
                  return;
                }
                updateStickFromPointer(e, leftJoystickRef, moveStickRef, setMoveStickUi);
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                resetStick(moveStickRef, setMoveStickUi);
              }}
              onPointerCancel={(e) => {
                e.preventDefault();
                resetStick(moveStickRef, setMoveStickUi);
              }}
            >
              <div
                className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-100/60 bg-cyan-200/25 backdrop-blur-sm"
                style={{ transform: `translate(calc(-50% + ${moveStickUi.x}px), calc(-50% + ${moveStickUi.y}px))` }}
              />
            </div>
          </div>
          <div className="absolute right-5 bottom-6 pointer-events-auto">
            <div className="text-[10px] text-white/70 uppercase tracking-wide mb-2 text-right">Aim / Cast</div>
            <div
              ref={rightJoystickRef}
              className="relative h-28 w-28 rounded-full border border-white/30 bg-slate-900/35 backdrop-blur-sm"
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => {
                e.preventDefault();
                updateStickFromPointer(e, rightJoystickRef, aimStickRef, setAimStickUi);
              }}
              onPointerMove={(e) => {
                e.preventDefault();
                if (!aimStickRef.current.active) {
                  return;
                }
                updateStickFromPointer(e, rightJoystickRef, aimStickRef, setAimStickUi);
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                resetStick(aimStickRef, setAimStickUi, true);
              }}
              onPointerCancel={(e) => {
                e.preventDefault();
                resetStick(aimStickRef, setAimStickUi);
              }}
            >
              <div
                className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-yellow-100/60 bg-yellow-200/25 backdrop-blur-sm"
                style={{ transform: `translate(calc(-50% + ${aimStickUi.x}px), calc(-50% + ${aimStickUi.y}px))` }}
              />
            </div>
          </div>
          <div className="absolute right-[9.3rem] bottom-10 pointer-events-auto">
            <button
              type="button"
              className={`h-14 w-14 rounded-full border text-[10px] uppercase tracking-wide ${
                shieldButtonActive
                  ? 'border-cyan-100/80 bg-cyan-300/35 text-cyan-50'
                  : 'border-cyan-100/45 bg-cyan-300/15 text-cyan-100'
              } backdrop-blur-sm`}
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => {
                e.preventDefault();
                shieldTouchRef.current = true;
                setShieldButtonActive(true);
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                shieldTouchRef.current = false;
                setShieldButtonActive(false);
              }}
              onPointerCancel={(e) => {
                e.preventDefault();
                shieldTouchRef.current = false;
                setShieldButtonActive(false);
              }}
              onPointerLeave={() => {
                shieldTouchRef.current = false;
                setShieldButtonActive(false);
              }}
            >
              Shield
            </button>
          </div>
        </div>
      ) : null}
      {pauseMenuOpen ? (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/45 backdrop-blur-sm">
          <div className="w-[min(92vw,30rem)] border border-cyan-100/35 bg-slate-900/35 backdrop-blur-md p-6 text-center text-white shadow-[0_0_30px_rgba(34,211,238,0.2)]">
            <div className="mb-4 text-3xl font-bold uppercase tracking-widest text-cyan-100 drop-shadow-[0_0_10px_rgba(165,243,252,0.5)]">Paused</div>
            <div className="mb-6 text-xs uppercase tracking-wide text-slate-300">
              Press ESC to resume with countdown
            </div>
            <div className="mb-6 border border-white/20 bg-slate-950/35 px-4 py-3 text-left text-xs text-slate-200">
              <div className="font-semibold text-slate-100 mb-2">Match Info</div>
              <div>Win Score: {WIN_SCORE}</div>
              <div>Background: {chosenBackground ?? 'loading'}</div>
              <div>Mode: {mergedConfig.mode === 'matchmaking' ? 'Matchmaking' : 'Vs AI'}</div>
              {mergedConfig.mode !== 'matchmaking' ? (
                <div>AI Difficulty: {mergedConfig.aiDifficulty ?? 3}</div>
              ) : null}
              <div>Local Side: {mergedConfig.localPlayer ?? 'player1'}</div>
              <div>Status: {connectionStatus}</div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                className="border border-yellow-100/45 bg-yellow-200/10 backdrop-blur-sm px-4 py-2 text-sm uppercase tracking-wide text-yellow-100 hover:bg-yellow-200/20"
                onClick={() => resumeFromPauseRef.current()}
              >
                Resume Match
              </button>
              {onExit ? (
                <button
                  type="button"
                  className="border border-rose-100/45 bg-rose-200/10 backdrop-blur-sm px-4 py-2 text-sm uppercase tracking-wide text-rose-100 hover:bg-rose-200/20"
                  onClick={() => exitMatchRef.current()}
                >
                  Exit To Menu
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {resumeCountdown !== null ? (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/35 backdrop-blur-[2px] pointer-events-none">
          <div className="text-7xl font-extrabold text-yellow-300 drop-shadow-[0_0_12px_rgba(253,224,71,0.7)]">
            {resumeCountdown}
          </div>
        </div>
      ) : null}
      {roundCountdownText !== null && gameState.gameStatus !== 'ended' ? (
        <div
          className={`absolute inset-0 flex items-center justify-center pointer-events-none ${
            goalCelebrationActive
              ? 'bg-amber-950/35 backdrop-blur-[2px]'
              : 'bg-slate-950/28 backdrop-blur-[1px]'
          }`}
        >
          <div className="text-center">
            <div
              className={`font-extrabold ${
                goalCelebrationActive
                  ? 'text-8xl text-amber-200 drop-shadow-[0_0_18px_rgba(251,191,36,0.9)]'
                  : 'text-7xl text-violet-200 drop-shadow-[0_0_14px_rgba(196,181,253,0.85)]'
              }`}
            >
              {roundCountdownText}
            </div>
          </div>
        </div>
      ) : null}
      {gameState.gameStatus === 'ended' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/45 backdrop-blur-sm">
          <div className="rounded-lg border border-white/25 bg-slate-900/40 backdrop-blur-md px-6 py-5 text-center text-white shadow-[0_0_20px_rgba(15,23,42,0.4)]">
            <div className="text-2xl font-semibold mb-2">
              {gameState.winner === 'red' ? 'Left Team Wins' : 'Right Team Wins'}
            </div>
            <button
              type="button"
              className="rounded-md bg-yellow-400 px-4 py-2 text-black font-semibold"
              onClick={() => setMatchSeedBump((prev) => prev + 1)}
            >
              Play Next Match
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PortalPongGame;